import dayjs from "dayjs";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import exampleTheme from "../../../../editor/themes/ExampleTheme";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { MathNode } from "../MathNode";
import { ImageNode } from "../ImageNode";
import { IframeNode } from "../IframeNode.js";
import { YouTubeTimestampNode } from "../YouTubeTimestampNode.js";
import moment from "moment";
import {
  hasPendingImages,
  resolveAnswerFromHistories,
  stripPendingImageBlocks,
} from "./utils/lessonImagePlaceholder";
import {
  checkifDateisAfterToday,
  isRequired,
  parseKaTeX,
} from "../../../../../../../utils/validator";
import { format } from "date-fns";
import { GenerationType } from "../../../../../../../utils/consts";
import { useMediaQuery, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getLessonAudio } from "../../../../../../../state/actions/getLessonAudioSocketAction";
import { useAuth } from "../../../../../../../features/auth/AuthContext";
import { toast } from "react-toastify";
import {
  getAssignmentTypeName,
  getEditorContent,
  removeCloseImageSpans,
  removeIframeCloseButtons,
  stripStyles,
} from "./utils/fileHelpers";
import { markdownAnswerToHtml } from "./utils/generateAllMarkdownToHtml.js";
import { useLessonWebSocket } from "./LessonWebSocket/useLessonWebSocket";
import api, { wsURL } from "../../../../../../../api/api";
import useDictation from "../../../../../../../hooks/useDictation";
import { formatDictation, endSentence } from "../../../../../../../utils/formatDictation";
import useAssignmentRubricSubmit from "./hook/useAssignmentRubricSubmit.js";
import useStorySetup, {
  getLessonStorageKey,
} from "./LessonGenerator/useStorySetup.js";
import { useReformatterWebSocket } from "./hook/useReformatterWebSocket.js";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext.js";
import { $getSelection, $isRangeSelection } from "lexical";
import { $generateHtmlFromNodes } from "@lexical/html";
import { useLexicalMathNodeHandler } from "./hook/useLexicalMathNodeHandler.js";
import { htmlImportConfig } from "../../../../editor/htmlConfig.js";
import { setcourseCheckPublishtoSchoologydtls } from "../../../../../../../state/actions/courseCheckPublishtoSchoologyAction.ts";
import { LessonSkipPopup } from "./childs/LessonSkipPopup.jsx";
import { useRepoQuery } from "../../../../../../../components/FileRepo/hooks/useRepoQuery.js";
import { convertToLexicalHtml } from "../../lessons/NewLessons/utils/convertToLexicalHtml.js";
import { addBreakBeforeGraphImages } from "../../../../../../../utils/utlis.js";
import { isGeneratedLessonName } from "../../../../../../../utils/lessonName.js";
import { AssignmentType } from "../../../../../../../utils/consts.js";
// 1️⃣ Create the Context

/**
 * Emptiness has to be content-aware: an image / iframe / table-only workboard is
 * real content but has zero text nodes, so textContent reports it as empty.
 * Close-button icons (data-internal) are editor chrome, not content.
 */
const hasWorkboardContent = (html) => {
  if (html == null) return false;

  let raw = Array.isArray(html) ? html.join("") : html;
  if (raw && typeof raw === "object") raw = raw.data ?? "";
  raw = String(raw);
  if (raw.trim() === "") return false;

  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    const media = doc.body.querySelector(
      'img:not([data-internal="true"]), iframe, video, audio, table, .math-node',
    );
    if (media) return true;
    return (doc.body.textContent || "").trim() !== "";
  } catch (err) {
    console.error("hasWorkboardContent parse failed:", err);
    return true; // fail open — a parse error must never lock the Save button
  }
};

const NewLessonsContext = createContext();

/**
 * Which generation flow a lesson belongs to. `lower_school` comes down with the
 * lesson details (and goes back up on every lesson save). Only a true flag opens
 * the story flow — no flag means content generation.
 */
const resolveGenerationType = (lessonDetails) =>
  lessonDetails?.lower_school === true
    ? GenerationType.STORY.value
    : GenerationType.CONTENT.value;

/** sessionStorage key for the teacher's manual generation-type pick. */
const GENERATION_TYPE_STORAGE_KEY = "lesson_generationType";

/** Generation type the teacher picked for this lesson before a refresh, if any. */
const readStoredGenerationType = (lessonKey) => {
  try {
    const stored = JSON.parse(
      sessionStorage.getItem(GENERATION_TYPE_STORAGE_KEY) || "null",
    );
    if (stored?.lessonKey !== lessonKey) return null;
    const isKnownType = Object.values(GenerationType).some(
      (type) => type.value === stored.value,
    );
    return isKnownType ? stored.value : null;
  } catch (error) {
    console.error("Failed to read stored generation type:", error);
    return null;
  }
};

// 2️⃣ Create a Provider Component
export const NewLessonsProvider = ({ children }) => {
  const theme = useTheme();
  const { token } = useAuth();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const [workboardText, setWorkboardText] = useState("");
  const [isWorkboardEmpty, setIsWorkboardEmpty] = useState(true);
  const [rubrics, setRubrics] = useState({});
  const dispatch = useDispatch();
  const [room_id, setRoom_id] = useState(
    () => sessionStorage.getItem("roomId") || "",
  );
  const savedData = sessionStorage.getItem("previewLessonData");
  location.state = savedData ? JSON.parse(savedData) : {};
  const [lesson, setLesson] = useState(location.state?.lesson || {});
  const [lesson_id, setlesson_id] = useState(lesson?.lesson_id ?? "");

  /** A lower school lesson is frozen once any student has submitted against it. */
  const isLockedBySubmissions = Boolean(
    lesson?.lower_school && lesson?.submission_count > 0,
  );
  const SUBMISSION_LOCKED_MESSAGE = "Editing is unavailable after submission";

  /**
   * True once this lesson has real saved content — either it arrived with a
   * description, or the create/update-lesson call has succeeded in this session.
   * A `lesson_id` alone is not enough: it exists from the moment the lesson row
   * is created, long before the teacher generates and saves anything.
   */
  const [isLessonSaved, setIsLessonSaved] = useState(() => {
    const description = location.state?.lesson?.description;
    return Boolean(description) && description !== "@$%^231#^#a5fgd%532^&4R3&";
  });
  const course = location.state?.course || {}; // default to Student if no role is passed
  const [isAssignment, setIsAssignment] = useState(false);
  /**
   * Content type picked from the chat-input menu (story vs. structured content).
   * The lesson itself decides the default: `lower_school` on the lesson details
   * means this is a story lesson. The flag is missing on a brand-new lesson, so
   * only an explicit boolean overrides the constant.
   */
  // A pick made before a page refresh wins over the lesson flag, same as a live pick.
  const [storedGenerationType] = useState(() =>
    readStoredGenerationType(getLessonStorageKey(location.state?.lesson)),
  );
  const [generationType, setGenerationTypeState] = useState(
    () => storedGenerationType ?? resolveGenerationType(location.state?.lesson),
  );
  /** Set once the type is settled — by the lesson flag or by the teacher. */
  const hasResolvedGenerationTypeRef = useRef(
    Boolean(storedGenerationType) ||
      typeof location.state?.lesson?.lower_school === "boolean",
  );

  const lessonStorageKey = getLessonStorageKey(lesson);
  const setGenerationType = useCallback(
    (value) => {
      // A manual pick wins over anything the lesson details say later.
      hasResolvedGenerationTypeRef.current = true;
      setGenerationTypeState(value);
      try {
        sessionStorage.setItem(
          GENERATION_TYPE_STORAGE_KEY,
          JSON.stringify({ lessonKey: lessonStorageKey, value }),
        );
      } catch (error) {
        console.error("Failed to persist generation type:", error);
      }
    },
    [lessonStorageKey],
  );

  // Lesson details can land after mount (navigation without router state, or a
  // refetch) — adopt the flag the first time it shows up as a real boolean.
  useEffect(() => {
    if (hasResolvedGenerationTypeRef.current) return;
    if (typeof lesson?.lower_school !== "boolean") return;
    hasResolvedGenerationTypeRef.current = true;
    setGenerationTypeState(resolveGenerationType(lesson));
  }, [lesson?.lower_school]);
  const [generateAllMode, setGenerateAllMode] = useState(false);
  const [stopGenerateAllMode, setStopGenerateAllMode] = useState(false);
  const [typeChanged, setTypeChanged] = useState(false);
  /** Drives websocket `task_name` during Generate All orchestration (Lesson → Assignment → Rubric). */
  const generateAllTaskNameRef = useRef(null);
  const prevGanrateLessonRef = useRef(false);
  const generateAllPersistStartedRef = useRef(false);
  /** Assignment id from `create-assignment` during Generate All (for rubric payload). */
  const generateAllAssignmentIdRef = useRef(null);
  /** In-flight saves so later steps can await ids if the next WS finishes first. */
  const generateAllLessonSavePromiseRef = useRef(Promise.resolve());
  const generateAllAssignmentSavePromiseRef = useRef(Promise.resolve());
  /** Registered by NewLessonEntry: refresh assignments / rubrics after create APIs. */
  const generateAllRefetchAfterAssignmentRef = useRef(null);
  const generateAllRefetchAfterRubricRef = useRef(null);
  /** Prevents duplicate end-of-pipeline refetch if persist is triggered more than once. */
  const generateAllRefetchDoneRef = useRef(false);
  /** Once rubric create-rubric finishes for this Generate All run, never call flush again. */
  const generateAllPersistCompletedRef = useRef(false);
  /** After create-rubric API — triggers Lexical insert with returned HTML. */
  const [generateAllRubricEditorSync, setGenerateAllRubricEditorSync] =
    useState(null);
  const [isRubric, setIsRubric] = useState(false);

  // Create a URLSearchParams object from the query string
  const queryParams = new URLSearchParams(location.search);

  /** Resolves to the merged answer HTML; awaited by the Generate All persist chain. */
  const lessonImagesPromiseRef = useRef(Promise.resolve(null));
  const handledImageTurnsRef = useRef(new Set());
  const turnIdemKeysRef = useRef(new Map());
  const prevGanrateForImagesRef = useRef(false);
  const [isResolvingImages, setIsResolvingImages] = useState(false);

  // Get the 'skip_lesson' parameter
  const showLesson = queryParams.get("skip_lesson") || false;
  const [formLessonError, setFormLessonErrorState] = useState({
    name: "",
    start_date: "",
    end_date: "",
  });
  const [chatHist, setChatHist] = useState([]);
  /**
   * The lesson chat is fetched after mount, so `chatHist` is empty for a beat
   * even on a lesson that already has a conversation. Anything branching on
   * "is this lesson empty?" has to wait for this to go false, otherwise it
   * flashes the empty state before the history lands.
   */
  const [isChatHistLoading, setIsChatHistLoading] = useState(true);

  const hasSeededLessonRef = useRef(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedRepoFiles, setSelectedRepoFiles] = useState([]);
  const [youtubeLinks, setYoutubeLinks] = useState([]);
  const [getSavedYoutubeLinks, setGetSavedYoutubeLinks] = useState("");
  const [defaultRepoFiles, setDefaultRepoFiles] = useState(
    course?.course_file_urls || [],
  );
  const [defaultYoutubeLink, setDefaultYoutubeLink] = useState([]);
  const [progressTracker, setprogressTracker] = useState([]);
  const [reFormatterLoading, SetReFormatterLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  /** Scroll only the chat panel — never the main page. */
  const scrollChatToBottom = useCallback((behavior = "smooth") => {
    const container = chatContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  const scrollChatContainerToBottom = useCallback(() => {
    // requestAnimationFrame(() => scrollChatToBottom("smooth"));
    // requestAnimationFrame(() => scrollChatToBottom("auto"));
    requestAnimationFrame(() => {
      // Re-check inside the frame: the user may have scrolled up between the
      // caller's gate check and this callback. Without this, a queued scroll
      // snaps them back to the bottom and undoes their scroll-up.
      if (showScrollButtonRef.current) return;
      scrollChatToBottom("smooth");
    });
  }, [scrollChatToBottom]);

  const makeIdempotencyKey = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const [isGenerating, setIsGenerating] = useState(false); // Track if the lesson is still generating
  const [showScrollButton, setShowScrollButton] = useState(false); // Track visibility of scroll
  const [checkAssignmentopen, setCheckAssignmentOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [rubric_id, setrubric_id] = useState("");
  const [myRole, setMyRole] = useState(null);
  // Course language code (same key as assignments) used for voice input.
  // Defaults to English until/unless the course provides one.
  const DEFAULT_DICTATION_LANGUAGE = "en-US";
  const [courseLanguage, setCourseLanguage] = useState(DEFAULT_DICTATION_LANGUAGE);
  const [assignment_id, setassignment_id] = useState("");
  const [previesAssignment, setPreviesAssignment] = useState(false);
  const [previesRubric, setPreviesRubric] = useState(false);
  const [socketDisconnected, setSocketDisconnected] = useState(false);
  const [eachAssignment_id, setEachAssignment_id] = useState(null);
  const [eachRubric_id, setEachRubric_id] = useState(null);
  const [defaultWorkboardData, setDefaultWorkboardData] = useState({
    LESSON: {
      name: "LESSON",
      isChanged: false,
      data: "",
    },
    ASSIGNMENT: {
      name: "ASSIGNMENT",
      isChanged: false,
      data: "",
    },
    RUBRIC: {
      name: "RUBRIC",
      isChanged: false,
      data: "",
    },
  });

  const [globleSaveData, setGlobleSaveData] = useState({
    LESSON: {
      name: "Lesson",
      data: "",
    },
    ASSIGNMENT: {
      name: "Assignment",
      data: [],
      formSaveAssignment: [],
    },
    RUBRIC: {
      name: "Rubric",
      data: [],
    },
  });

  const chatDrawerState = useSelector((state) => state?.ChatDrawerState);
  const [getassignmentdetails, setGetAssignmentdetails] = useState([]);
  const [assingmentListOfID, setAssingmentListOfID] = useState([]);
  const [assignmentsCountTwo, setAssignmentCountsTwo] = useState(0);
  const [rubricCounttTwo, setRubricCountTwo] = useState(0);
  const [istoggleClick, setIstoggleClick] = useState(false);
  const [globleOnChangeDataSave, setGlobleOnChangeDataSave] = useState(false);
  const [clickSaveBTN, setClickSaveBTN] = useState(false);
  const [clickSaveRubricBTN, setClickSaveRubricBTN] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSaveChecker, setisSaveChecker] = useState(false);
  const [showCourseSuccessfully, setShowCourseSuccessfully] = useState(false);
  const [loadingPublishSchoology, setLoadingPublishSchoology] = useState(false);
  const [disabledRubric, setDisabledRubric] = useState(false);
  const [getSpecificAssignmentType, setGetSpecificAssignmentType] =
    useState(null);
  const [assignmentsCount, setAssignmentCounts] = useState([]);
  const [showAssingment, setShowAssingment] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState("Assignment");
  const [onChangeAssignmentData, setOnChangeAssignmentData] = useState([]);
  const [editWorkboardStatues, setEditWorkboardStatues] = useState(false);
  const [rubricCount, setRubricCount] = useState([]);
  const [onChangeRubricData, setOnChangeRubricData] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState("Rubric");
  const [showDropdownRubric, setShowDropdownRubric] = useState(false);
  const [updateFuntion, setUpdateFuntion] = useState(false);
  const [isRubricConfirmModalData, setIsRubricConfirmModalData] = useState({
    selectedRubric: null,
    selectedRubricIndex: null,
  });

  const [isAssingmentConfirmModalData, setIsAssingmentConfirmModalData] =
    useState({
      selectedAssingment: null,
      selectedAssingmentIndex: null,
    });
  const [globleOnChange, setGlobleOnChange] = useState({
    LESSON: {
      name: "Lesson",
      data: "",
    },
    ASSIGNMENT: {
      name: "Assignment",
      data: [],
      formAssignment: [],
    },
    RUBRIC: {
      name: "Rubric",
      data: [],
    },
  });

  const [showRubricPopUp, setShowRubricPopUp] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false); // State to control visibility
  const [showDownloadFile, setshowDownloadFile] = useState(false);
  const modalRef = useRef();
  const modalContentRef = useRef(); // Reference to the modal content
  const [fileNameError, setFileNameError] = useState("");
  const [fileNameDownload, setFileNameDownload] = useState("");
  const [getschoologystatus, setschoologystatus] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [showInputTooltip, setShowInputTooltip] = useState(false);
  const [latextextvalue, setLatextextvalue] = useState("");
  const [oldMathNode, setOldMathNode] = useState(null);
  const [oldnodeeditor, setOldNodeEditor] = useState(null);
  const [getReformatterSocket, setGetReformatterSocket] = useState(null);
  const [reFormatterData, setReFormatterData] = useState("");
  const [reformatterReconnectAttempts, setReformatterReconnectAttempts] =
    useState(0);
  const shouldReconnectRef = useRef(true); // Use ref to persist value across renders

  const [afterConfirmation, setAfterConfirmation] = useState(null);
  const [callAddAssignmentFun, setCallAddAssignmentFun] = useState(false);
  const [callSwitchToRubricFun, setCallSwitchToRubricFun] = useState(false);
  /** `{ assignment, index }` — triggers `handleAssignmentClick` in UpdateActiveWorkboard (e.g. existing lesson save → select first assignment). */
  const [pendingAssignmentClickArgs, setPendingAssignmentClickArgs] =
    useState(null);
  const youtubeLinksRef = useRef([]);
  const [isNotSavePopUp, setIsNotSavePopUp] = useState(false);
  const [showloading, setshowloading] = useState(false);
  const [editloading, setEditLoading] = useState(false);
  const [skipLesson, setskipLesson] = useState(false);
  const [skipLessonSession, setskipLessonSession] = useState(false);
  const globalState = useSelector((state) => state.globalState);

  /** After "Create Manually", show the workboard Lexical editor instead of the empty-state splash. */
  const [createManuallyMode, setCreateManuallyMode] = useState(false);
  const [hideTimerStates, setHideTimerStates] = useState({});
  const [currentHideTimerState, setCurrentHideTimerState] = useState(false);

  const textBoxRef = useRef(null);
  const [pop_up_for_lesson_start_date_note, setPopupForLessonStartDateNote] =
    useState(false);
  const [formAssignmentState, setFormAssignmentState] = useState({
    name: null,
    assignment: null,
    assignment_json: null,
    lesson: null,
    duration_minutes: null,
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: null,
    type: null,
  });

  const EMPTY_LESSON_PLACEHOLDER_DESCRIPTION_two =
    "This lesson was skipped to directly create an assignment.";

  const [formErrorAssignmentState, setFormErrorAssignmentState] = useState({
    name: "",
    assignment: "",
    lesson: "",
    duration_minutes: "",
    start_date: "",
    end_date: "",
    type: "",
  });

  const [formState, setFormState] = useState(() => {
    const savedFormData = sessionStorage.getItem("lesson_formState");
    const lessonDetails = sessionStorage.getItem("lessonDetails");

    var _savedFormData = {};
    if (savedFormData) {
      _savedFormData = JSON.parse(savedFormData);
      const roomid_saved = sessionStorage.getItem("roomId");
      if (roomid_saved) {
        setRoom_id(roomid_saved);
      }
    }

    if (savedFormData && lessonDetails) {
      if (
        JSON.parse(savedFormData).description !== JSON.parse(lessonDetails).data
      ) {
        _savedFormData.description = JSON.parse(lessonDetails).data;
      }
    }
    return {
      ...(_savedFormData.start_date
        ? _savedFormData
        : {
          // A generated placeholder opens as an empty box so the teacher names
          // the lesson themselves; a real name is kept as-is.
          name: isGeneratedLessonName(lesson?.name) ? "" : lesson?.name ?? null,
          assignment_no: null,
          start_date: lesson?.start_date ?? dayjs().format("YYYY-MM-DD"),
          end_date: lesson?.end_date ?? null,
          description: lesson?.description ?? null,
          chat_query: null,
          chat_hist: null,
        }),
    };
  });

  /**
   * Reconcile the lesson name against the server once, on mount.
   *
   * Everything above seeds from sessionStorage, and sessionStorage is per-tab:
   * window.open hands the new tab a *copy* of the opener's storage at the
   * moment it opens, and nothing updates it afterwards. A generator tab opened
   * before a rename therefore shows the old name for as long as it stays open,
   * and reloading re-reads the same frozen copy rather than fetching anything.
   *
   * The lesson_id in the URL is the one value that cannot go stale, so it is
   * used to ask the server what this lesson is actually called.
   *
   * Only an untouched field is corrected. If the teacher has already typed
   * something by the time the response lands, their text wins — a background
   * refresh must never overwrite work in progress.
   */
  // Runs once, but only once both the id and the auth token exist. The token
  // arrives from context and is not necessarily set on the first render, so an
  // effect with no dependencies would check too early, bail, and never retry —
  // which is exactly what happened in testing: the request was never sent.
  const didReconcileLessonNameRef = useRef(false);
  useEffect(() => {
    if (didReconcileLessonNameRef.current) return;

    const seededName = formState?.name ?? null;
    const id =
      lesson_id ||
      new URLSearchParams(window.location.search).get("lesson_id");
    if (!id || !token) return;

    didReconcileLessonNameRef.current = true;
    let cancelled = false;

    api
      .get(`lessons/get-lesson/${id}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        const fresh = res?.data?.data;
        const serverName = fresh?.name;
        if (!serverName) return;

        setLesson((prev) => ({ ...prev, ...fresh }));
        setFormState((prev) => {
          // Untouched means still exactly what the snapshot seeded.
          if ((prev?.name ?? null) !== seededName) return prev;
          const next = isGeneratedLessonName(serverName) ? "" : serverName;
          return next === prev?.name ? prev : { ...prev, name: next };
        });
      })
      .catch(() => {
        // A failed refresh leaves the snapshot in place. Being unable to
        // confirm the name is not a reason to block the teacher from working.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, lesson_id]);

  /** Story Setup panel state (difficulty / learning objectives / description). */
  const storySetup = useStorySetup({
    lessonName: formState?.name,
    courseId: course?.course_id,
    token,
    savedLesson: lesson,
  });

  /**
   * Lower School fields saved alongside the lesson. `lower_school` always goes
   * out so switching back to content generation clears the flag; the story
   * fields themselves are only sent for the story flow.
   */
  const getLowerSchoolPayload = () => {
    const isStoryGeneration = generationType === GenerationType.STORY.value;
    return {
      lower_school: isStoryGeneration,
      ...(isStoryGeneration && {
        difficulty: storySetup.storyDifficulty,
        learning_objective: storySetup.selectedObjectives,
        learning_objective_description: storySetup.storyDescription,
      }),
    };
  };

  const [isSwitchOnVirtualAssistant, setIsSwitchOnVirtualAssistant] = useState(
    lesson?.virtual_assistant ?? true,
  );

  const [isSwitchOn, setIsSwitchOn] = useState(lesson?.is_personalize ?? false);
  const [isSwitchOn2, setIsSwitchOn2] = useState(
    lesson?.is_file_download ?? false,
  );
  const [isSwitchOnLessonPreview, setIsSwitchOnLessonPreview] = useState(
    lesson?.lesson_preview ?? false,
  );
  const [showPopup, setShowPopup] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false); // need to change name of this state
  const adminRole = globalState?.role === "Admin";

  const teacher_id =
    location.state?.teacher_id ??
    globalState.teacher_details?.data?.teacher_id ??
    "";
  const [showCloseBTN, setShowCloseBTN] = useState(false);
  const [ganrateLesson, setGanrateLesson] = useState(false);

  const [isPopupVisible, setPopupVisible] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(null);
  const [SkipLessonflag, setskipLessonflag] = useState(false);
  const [generateImageOpen, setGenerateImageOpen] = useState(false);

  const [uploadedFileLink, setuploadedFileLink] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showUploadPop, setShowUploadPop] = useState(false);
  const showScrollButtonRef = useRef(false);
  const lastScrollTopRef = useRef(0); // previous scrollTop, to detect scroll direction
  const heldTopRef = useRef(0); // position to hold at while the user has scrolled up
  const pinRafRef = useRef(null);
  // True while the pointer rests on an embedded video iframe in the chat panel
  const pointerOverMediaRef = useRef(false);
  // True while the user is actively dragging (mouse/pointer down) — e.g. holding
  // the native scrollbar thumb. While this is set we stand down from pinning so
  // the drag isn't yanked back; on release we recompute the stick state.
  const isPointerDownRef = useRef(false);
  // Guards the one-time "jump to latest message" on initial lesson load. Re-armed
  // whenever the lesson changes so each opened lesson lands at its last message.
  const didInitialScrollRef = useRef(false);

  const [workboards, setWorkboards] = useState({
    LESSON: {
      name: "Lesson",
      data: "",
    },
    ASSIGNMENT: {
      name: "Assignment",
      data: assignments?.length > 0 ? assignments : "",
    },
    RUBRIC: {
      name: "Rubric",
      data: rubrics?.length > 0 ? rubrics : "",
    },
  });

  const workboardsRef = useRef(workboards);
  const formStateRef = useRef(formState);
  const formAssignmentStateRef = useRef(formAssignmentState);
  const lessonIdRef = useRef(lesson_id);
  const chatHistRef = useRef(chatHist);
  const assignmentsCountTwoRef = useRef(0);
  const rubricCounttTwoRef = useRef(0);
  const folderId = JSON.parse(sessionStorage.getItem("previewLessonData"))
    ?.lesson?.folder_id;

  const {
    data: repoData,
    isLoading,
    isFetching,
  } = useRepoQuery({
    token,
    endpoint: "manual-upload/folder-contents/",
    queryKey: "folderContents",
    folderId: folderId || null,
    is_all: true,
  });

  useEffect(() => {
    workboardsRef.current = workboards;
    formStateRef.current = formState;
    formAssignmentStateRef.current = formAssignmentState;
    lessonIdRef.current = lesson_id;
    chatHistRef.current = chatHist;
    assignmentsCountTwoRef.current = assignmentsCountTwo;
    rubricCounttTwoRef.current = rubricCounttTwo;
  });

  useEffect(() => {
    if (repoData && folderId && repoData?.files) {
      setDefaultRepoFiles((prev) => [...prev, ...repoData?.files]);
    }
  }, [repoData, folderId]);

  const [assignmentDuration, setAssignmentDuration] = useState({
    minutes: 0,
  });

  const {
    socket,
    combinedMessageRef,
    closeConnection,
    isLessonSocketReady,
    ensureSocketConnected,
  } = useLessonWebSocket({
    room_id,
    lesson,
    wsURL,
    course,
    teacher_id,
    scrollChatContainerToBottom,
    showScrollButtonRef,
    setChatHist,
    setShowScrollButton,
    setShowCloseBTN,
    setStopGenerateAllMode,
    setGenerateAllMode,
    generateAllMode,
    setGanrateLesson,
    setIsGenerating,
    setSelectedFiles,
    setuploadedFileLink,
    token,
    setSocketDisconnected,
  });

  useEffect(() => {
    if (!isSwitchOn) {
      setShowPopup(false); // Hide the popup when the switch is turned on
    }
  }, [isSwitchOn]);

  // useEffect(() => {
  //   let scrollInterval;

  //   if (showCloseBTN) {
  //     // only scroll immediately if user hasn't manually scrolled up
  //     if (!showScrollButtonRef.current) {
  //       scrollChatContainerToBottom();
  //     }

  //     scrollInterval = setInterval(() => {
  //       // read ref every tick — no stale closure, no dep-array churn
  //       if (!showScrollButtonRef.current) {
  //         scrollChatContainerToBottom();
  //       }
  //     }, 1500);
  //   }

  //   return () => {
  //     if (scrollInterval) clearInterval(scrollInterval);
  //   };
  // }, [showCloseBTN, scrollChatContainerToBottom]); // deps unchanged

  // // Keep chat pinned to bottom while streaming unless the user scrolled up.
  // useEffect(() => {
  //   if (!ganrateLesson || showScrollButton) return;
  //   scrollChatContainerToBottom();
  // }, [chatHist, ganrateLesson, showScrollButton, scrollChatContainerToBottom]);
  useEffect(() => {
    if (!ganrateLesson || showScrollButton || showScrollButtonRef.current)
      return;
    scrollChatContainerToBottom();
  }, [chatHist, ganrateLesson, showScrollButton, scrollChatContainerToBottom]);

  // Re-arm the initial jump each time a different lesson is opened.
  useEffect(() => {
    didInitialScrollRef.current = false;
  }, [lesson?.lesson_id]);

  // On first load of a saved lesson, land on the LATEST message (bottom) instead
  // of sitting at the first one. Runs once per lesson, and only when not
  // streaming and the user hasn't already scrolled up. Uses an instant jump
  // (no animation) and repeats briefly so late-laid-out math/images don't leave
  // us short of the true bottom.
  useEffect(() => {
    if (didInitialScrollRef.current) return;
    if (ganrateLesson || showScrollButtonRef.current) return;
    if (!Array.isArray(chatHist) || chatHist.length === 0) return;
    const container = chatContainerRef.current;
    if (!container) return;

    didInitialScrollRef.current = true;

    const jump = () => {
      const c = chatContainerRef.current;
      if (!c || showScrollButtonRef.current) return; // user scrolled up meanwhile
      c.scrollTop = c.scrollHeight;
      heldTopRef.current = c.scrollTop;
      lastScrollTopRef.current = c.scrollTop;
    };

    const raf = requestAnimationFrame(jump);
    const t1 = setTimeout(jump, 150);
    const t2 = setTimeout(jump, 400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [chatHist, ganrateLesson]);

  // We fully OWN the chat panel's scroll position. The browser's native
  // auto-scroll (focus/selection-into-view fired by the live streaming) keeps
  // dragging the panel to the bottom and cannot be intercepted via our scroll
  // functions — so instead we drive scrollTop ourselves from the real input
  // events (wheel/touch) and, while the user has scrolled up, pin scrollTop to a
  // held position on every scroll event so nothing can move it.
  const pinLoop = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c || !showScrollButtonRef.current) {
      pinRafRef.current = null;
      return;
    }
    // While the user is dragging the scrollbar, let them move freely and keep the
    // held position synced to where they drag — don't yank scrollTop back.
    // dragging the scrollbar, or wheel chained from a hovered video → don't yank back
    if (isPointerDownRef.current || pointerOverMediaRef.current) {
      heldTopRef.current = c.scrollTop;
    } else if (c.scrollTop !== heldTopRef.current) {
      c.scrollTop = heldTopRef.current;
    }
    pinRafRef.current = requestAnimationFrame(pinLoop);
  }, []);

  const ensurePinLoop = useCallback(() => {
    if (pinRafRef.current == null) {
      pinRafRef.current = requestAnimationFrame(pinLoop);
    }
  }, [pinLoop]);
  // Programmatic scroll (e.g. a YouTube timestamp click jumping to its video).
  // Plain scrollIntoView gets yanked back by the pin above, so animate the held
  // position itself and detach from the bottom like a user scroll-up would.
  const scrollAnimRafRef = useRef(null);
  const scrollChatToElement = useCallback(
    (el) => {
      const c = chatContainerRef.current;
      if (!c || !el || !c.contains(el)) return;
      if (scrollAnimRafRef.current != null) cancelAnimationFrame(scrollAnimRafRef.current);

      const maxScroll = Math.max(0, c.scrollHeight - c.clientHeight);
      const elRect = el.getBoundingClientRect();
      const cRect = c.getBoundingClientRect();
      // center the element in the panel
      const target = Math.min(
        Math.max(c.scrollTop + (elRect.top - cRect.top) - (c.clientHeight - elRect.height) / 2, 0),
        maxScroll,
      );

      const atBottom = maxScroll - target < 12;
      showScrollButtonRef.current = !atBottom;
      setShowScrollButton(!atBottom);

      const from = c.scrollTop;
      const start = performance.now();
      const duration = 450;
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        const top = from + (target - from) * eased;
        heldTopRef.current = top;
        lastScrollTopRef.current = top;
        c.scrollTop = top;
        scrollAnimRafRef.current = p < 1 ? requestAnimationFrame(tick) : null;
      };
      scrollAnimRafRef.current = requestAnimationFrame(tick);
      if (!atBottom) ensurePinLoop();
    },
    [ensurePinLoop],
  );

  const applyScrollIntent = useCallback(
    (deltaY) => {
      const c = chatContainerRef.current;
      if (!c) return;
      // user input wins over an in-flight programmatic scroll
      if (scrollAnimRafRef.current != null) {
        cancelAnimationFrame(scrollAnimRafRef.current);
        scrollAnimRafRef.current = null;
      }
      const maxScroll = Math.max(0, c.scrollHeight - c.clientHeight);
      // If we were following the bottom, start from the real current position.
      if (!showScrollButtonRef.current) heldTopRef.current = c.scrollTop;
      const next = Math.min(
        Math.max(heldTopRef.current + deltaY, 0),
        maxScroll,
      );
      heldTopRef.current = next;
      if (deltaY < 0) {
        // Scrolling up → detach from the bottom, show the button.
        showScrollButtonRef.current = true;
        setShowScrollButton(true);
        ensurePinLoop();
      }
      if (maxScroll - next < 12) {
        // Reached the bottom → resume following.
        showScrollButtonRef.current = false;
        setShowScrollButton(false);
      }
      c.scrollTop = next;
      lastScrollTopRef.current = next;
    },
    [ensurePinLoop],
  );
  useEffect(() => {
    return () => {
      if (pinRafRef.current != null) cancelAnimationFrame(pinRafRef.current);
    };
  }, []);
  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    let touchY = 0;
    // Things rendered inside the panel can scroll on their own (the Story Setup
    // card, its description textarea). Wheel/touch bubbles up to here, so hand
    // the gesture back to the browser whenever an inner scroller can still
    // absorb it — otherwise the panel swallows every scroll and those elements
    // only move by dragging their scrollbar.
    const isConsumedByInnerScroller = (target, deltaY) => {
      if (!deltaY) return false;
      let node = target instanceof Element ? target : null;
      while (node && node !== el) {
        const maxScroll = node.scrollHeight - node.clientHeight;
        if (maxScroll > 1) {
          const { overflowY } = window.getComputedStyle(node);
          const scrolls =
            overflowY === "auto" ||
            overflowY === "scroll" ||
            node.tagName === "TEXTAREA";
          // Only yield while it can still move that way; at either end the
          // panel takes over, matching normal scroll chaining.
          if (
            scrolls &&
            (deltaY < 0 ? node.scrollTop > 0 : node.scrollTop < maxScroll - 1)
          ) {
            return true;
          }
        }
        node = node.parentElement;
      }
      return false;
    };
    // preventDefault so the browser's own wheel scroll doesn't fight ours.
    const onWheel = (e) => {
      if (isConsumedByInnerScroller(e.target, e.deltaY)) return;
      e.preventDefault();
      applyScrollIntent(e.deltaY);
    };
    const onTouchStart = (e) => {
      touchY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e) => {
      const y = e.touches[0]?.clientY ?? touchY;
      const deltaY = touchY - y; // finger up = scroll down
      touchY = y;
      if (isConsumedByInnerScroller(e.target, deltaY)) return;
      e.preventDefault();
      applyScrollIntent(deltaY);
    };
    // Pointer down/up on the panel: covers holding the native scrollbar thumb.
    // While down, pinning stands down (see pinLoop / handleUserScroll). On release
    // we recompute whether to resume stick-to-bottom or stay detached.
    const onPointerDown = (e) => {
      // Only treat a press on the vertical scrollbar gutter as a drag (pointer x
      // beyond the content box). Ordinary clicks / text selection inside the
      // panel must NOT disable pinning, or streaming follow could leak.
      const rect = el.getBoundingClientRect();
      if (e.clientX - rect.left > el.clientWidth) {
        isPointerDownRef.current = true;
      }
    };
    const onPointerUp = () => {
      if (!isPointerDownRef.current) return;
      isPointerDownRef.current = false;
      const c = chatContainerRef.current;
      if (!c) return;
      const maxScroll = Math.max(0, c.scrollHeight - c.clientHeight);
      heldTopRef.current = c.scrollTop;
      lastScrollTopRef.current = c.scrollTop;
      if (maxScroll - c.scrollTop < 12) {
        // Released at/near the bottom → resume following the stream.
        showScrollButtonRef.current = false;
        setShowScrollButton(false);
      } else {
        // Released scrolled-up → stay detached, keep the button, re-pin here.
        showScrollButtonRef.current = true;
        setShowScrollButton(true);
        ensurePinLoop();
      }
    };

    // Track the pointer resting on an embedded video (iframe or its scroll shield).
    // Over a cross-origin iframe our wheel listener never fires — the browser chains
    // the wheel natively into this panel instead — so handleUserScroll / pinLoop must
    // accept that scroll as user intent rather than pinning it back.
    const isMediaEl = (n) =>
      n instanceof Element && (n.tagName === "IFRAME" || n.dataset?.ytShield === "true");
    const onPointerOver = (e) => {
      if (isMediaEl(e.target)) pointerOverMediaRef.current = true;
    };
    const onPointerOut = (e) => {
      if (isMediaEl(e.target) && !isMediaEl(e.relatedTarget)) pointerOverMediaRef.current = false;
    };
    const onPointerLeave = () => {
      pointerOverMediaRef.current = false;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("pointerover", onPointerOver, { passive: true });
    el.addEventListener("pointerout", onPointerOut, { passive: true });
    el.addEventListener("pointerleave", onPointerLeave, { passive: true });
    // Listen on window so a release outside the panel still clears the flag.
    window.addEventListener("pointerup", onPointerUp, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointerover", onPointerOver);
      el.removeEventListener("pointerout", onPointerOut);
      el.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [applyScrollIntent, ensurePinLoop]);

  // Native scroll chained from a hovered video iframe: adopt it as the held position
  // and update follow/detach state exactly like a wheel scroll would.
  const adoptChainedScroll = (c) => {
    const top = c.scrollTop;
    const maxScroll = Math.max(0, c.scrollHeight - c.clientHeight);
    heldTopRef.current = top;
    if (maxScroll - top < 12) {
      if (showScrollButtonRef.current) {
        showScrollButtonRef.current = false;
        setShowScrollButton(false);
      }
    } else if (top < lastScrollTopRef.current && !showScrollButtonRef.current) {
      showScrollButtonRef.current = true;
      setShowScrollButton(true);
      ensurePinLoop();
    }
    lastScrollTopRef.current = top;
  };
  const handleUserScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;

    // While streaming, follow is forced — never show the button
    // if (ganrateLesson) {
    //   setShowScrollButton(false);
    //   return;
    // }

    // const distanceFromBottom =
    //   container.scrollHeight - container.clientHeight - container.scrollTop;
    // setShowScrollButton(distanceFromBottom > 400);
    // While the user has scrolled up, pin the panel to the held position so the
    // browser's native auto-scroll can't drag it back to the bottom. While
    // following (stick on), keep the held position synced to the real one so a
    // later wheel-up starts from the right place.
    // Wheel over a video iframe scrolls us natively — honour it in one go.
    if (pointerOverMediaRef.current && !isPointerDownRef.current) {
      adoptChainedScroll(container);
      return;
    }
    if (showScrollButtonRef.current) {
      // While the user drags the scrollbar, follow their position instead of
      // pinning — otherwise the thumb freezes. pointerup recomputes stick state.
      if (isPointerDownRef.current) {
        heldTopRef.current = container.scrollTop;
        lastScrollTopRef.current = container.scrollTop;
      } else {
        // Pin hard — set it every time (no tolerance) so a slow easing phantom
        // scroll can't leak through a pixel at a time.
        if (container.scrollTop !== heldTopRef.current) {
          container.scrollTop = heldTopRef.current;
        }
        lastScrollTopRef.current = heldTopRef.current;
      }
    } else {
      heldTopRef.current = container.scrollTop;
      lastScrollTopRef.current = container.scrollTop;
    }
  };

  const editorConfig = {
    theme: exampleTheme,
    // Keeps <pre>/multi-line <code> out of CodeNode - see htmlConfig.js.
    html: { import: htmlImportConfig },
    onError(error) {
      throw error;
    },
    editable: true,
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      MathNode,
      ImageNode,
      IframeNode,
      YouTubeTimestampNode,
    ],
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
      chat_hist: chatHist,
    }));
  };




  const TabToBottom = useCallback(() => {
    scrollChatContainerToBottom();
  }, [scrollChatContainerToBottom]);

  // Smooth auto-scroll: follows streamed content slowly while generating.
  useEffect(() => {
    if (!ganrateLesson) return;

    let rafId;

    const step = () => {
      const container = chatContainerRef.current;
      // only follow while attached to the bottom — never drag a scrolled-up user down
      if (container && !showScrollButtonRef.current) {
        const target = container.scrollHeight - container.clientHeight;
        const distance = target - container.scrollTop;

        if (distance > 1) {
          container.scrollTop += Math.max(1, distance * 0.06);
        }
      }
      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [ganrateLesson]);

  const currentDate = dayjs();
  const startDate = dayjs(formState.start_date);

  const minDate = currentDate.isAfter(startDate)
    ? currentDate.format("MMM DD, YYYY")
    : startDate.format("MMM DD, YYYY");

  const courseStartDate = dayjs(course?.start_date);

  const lessonMinDate = currentDate.isAfter(courseStartDate)
    ? currentDate.format("MMM DD, YYYY")
    : courseStartDate.format("MMM DD, YYYY");

  const isValidDateAssignmentStartDate = (currentDate, selectedDate) => {
    return currentDate.isSameOrAfter(minDate, "day");
  };
  const isValidDateAssignmentEndDate = (currentDate, selectedDate) => {
    // Choose the correct start date based on whether formAssignmentState?.start_date is defined
    const startDate = moment(
      formAssignmentState?.start_date
        ? formAssignmentState.start_date
        : formState.start_date,
    ).add(1, "days");

    // Disable dates that are before the start date (making only dates after start date selectable)
    return !currentDate.isBefore(startDate);
  };

  const isValidDateLessonStartDate = (currentDate) => {
    const endDate = course?.end_date ? moment(course?.end_date) : null;
    const lessonendDate = formState?.end_date
      ? moment(formState?.end_date)
      : null;
    const isAfterMinDate = currentDate.isSameOrAfter(lessonMinDate, "day");
    const isBeforeEndDate = lessonendDate
      ? lessonendDate
        ? currentDate.isSameOrBefore(lessonendDate, "day")
        : true
      : endDate
        ? currentDate.isSameOrBefore(endDate, "day")
        : true;
    return isAfterMinDate && isBeforeEndDate;
  };
  const isValidDateLessonEndDate = (currentDate, selectedDate) => {
    const startDate = moment(course?.start_date);
    const endDate = course?.end_date ? moment(course?.end_date) : null;
    const startDateLesson = moment(formState?.start_date ?? "");
    const today = moment(); // Get the current date
    // Check if endDate is provided and if it's not expired
    if (endDate && endDate.isBefore(today)) {
      return false; // endDate is expired, return false
    }

    // Disable dates between start and end dates, inclusive
    return endDate
      ? !currentDate.isBefore(startDate) &&
      !currentDate.isAfter(endDate) &&
      currentDate.isAfter(startDateLesson)
      : !currentDate.isBefore(startDate) &&
      currentDate.isAfter(startDateLesson);
  };

  const handleInputChangedate = (name, date) => {
    if (lesson_id !== "") {
      let errors = {};
      if (name === "end_date") {
        errors.end_date = checkifDateisAfterToday(
          moment(date).format("YYYY-MM-DD"),
        );
      }
      setFormLessonErrorState((prev) => ({ ...prev, [name]: errors[name] }));
    }

    if (name === "start_date") {
      const startDate = moment();
      const endDate = date;
      if (endDate.isAfter(startDate)) {
        setPopupForLessonStartDateNote(true);
        setTimeout(() => {
          setPopupForLessonStartDateNote(false);
        }, 3000);
      }
    }
    setFormState((prev) => ({
      ...prev,
      [name]: moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : "",
    }));
  };
  const isSmallScreen = useMediaQuery(theme?.breakpoints.down("lg"));
  const [activeTab, setActiveTab] = useState("lesson");
  const handleSwitchToggleVirtualAssistant = () => {
    const payload = {
      virtual_assistant: !isSwitchOnVirtualAssistant,
    };
    updateLesson(lesson_id, payload, "lesson", "ChatBot");
    setIsSwitchOnVirtualAssistant(!isSwitchOnVirtualAssistant);
  };
  const [Assignmentgeneration, setAssignmentgeneration] = useState(false);

  const handleSwitchToggleDownload = () => {
    const payload = {
      is_file_download: !isSwitchOn2,
    };
    updateLesson(lesson_id, payload, "lesson", "Download Files");

    setIsSwitchOn2(!isSwitchOn2);
  };

  const handleSwitchToggleLessonPreview = () => {
    setIsSwitchOnLessonPreview(!isSwitchOnLessonPreview);

    const payload = {
      lesson_preview: !isSwitchOnLessonPreview,
    };
    updateLesson(lesson_id, payload, "lesson", "Lesson Preview");
  };
  // Function for handling button click to scroll
  const handleScrollClick = () => {
    // TabToBottom();
    // Resume following FIRST — otherwise the gated auto-scroll bails (the button
    // is only visible while stick is off) and the click does nothing.
    showScrollButtonRef.current = false;
    setShowScrollButton(false);
    const c = chatContainerRef.current;
    if (c) {
      const bottom = Math.max(0, c.scrollHeight - c.clientHeight);
      heldTopRef.current = bottom;
      lastScrollTopRef.current = bottom;
      c.scrollTop = bottom;
    }
  };

  const getname = (name) => {
    var fullName = name;
    // Split the name into words
    const words = fullName?.split(" ");

    // Initialize variables for the first letter of the first and last names
    var firstNameInitial = "";
    var lastNameInitial = "";

    // Check if there is a last name
    if (words?.length > 1) {
      firstNameInitial = words[0]?.charAt(0)?.toUpperCase();
      lastNameInitial = words[1]?.charAt(0)?.toUpperCase();
    }

    // Combine the uppercase initials
    var initials = firstNameInitial + lastNameInitial;

    return initials === "" ? "T" : initials;
  };

  const [chatAudioId, setchatAudioId] = useState();
  const texttospeech = useSelector((state) => state.texttospeech);

  const generateAIVoice = (chat, id) => {
    setchatAudioId(id);



    let lessonData = parseKaTeX(chat);

    // Audio now streams over /ws/lesson_audio/<lesson_id>/. The chat uuid only
    // travels in the payload as generator_id, never in the socket URL.
    dispatch(
      getLessonAudio({
        text: lessonData,
        mode: "generator",
        generatorId: id,
        socketLessonId: lesson_id,
        token,
      })
    );
  };

  useEffect(() => {
    const lessonUrls = Array.isArray(lesson?.youtube_docx_urls)
      ? lesson.youtube_docx_urls
      : [];
    const courseUrls = Array.isArray(course?.youtube_docx_urls)
      ? course.youtube_docx_urls
      : [];

    if (lessonUrls.length > 0 || courseUrls.length > 0) {
      setDefaultYoutubeLink([...lessonUrls, ...courseUrls]);
    }
    // Serialize to string — React compares primitives by value, not reference
  }, [
    JSON.stringify(lesson?.youtube_docx_urls),
    JSON.stringify(course?.youtube_docx_urls),
  ]);

  const onCopyClicked = (chat, index) => {
    const textArea = document.createElement("textarea");
    textArea.value = addBreakBeforeGraphImages(chat.answer);

    document.body.appendChild(textArea);

    textArea.select();

    try {
      // Check if the Clipboard API is available
      if (navigator.clipboard && window.isSecureContext) {
        // Use the Clipboard API to copy the selected text to the clipboard
        navigator.clipboard
          .writeText(textArea.value)
          .then(() => {
            toast.success("Text copied to clipboard", {
              position: "top-center",
            });
          })
          .catch((error) => {
            toast.error("Failed to copy text", {
              position: "top-center",
            });
          });
      } else {
        // Fallback method for non-SSL environments
        document.execCommand("copy");
        toast.success("Text copied to clipboard", {
          position: "top-center",
        });
      }
    } catch (error) {
      toast.error("Failed to copy text", {
        position: "top-center",
      });
    } finally {
      // Remove the textarea element
      document.body.removeChild(textArea);
    }
  };

  //bb
  const [activeWorkboard, setActiveWorkboard] = useState(() => {
    const skipLessonStored = sessionStorage.getItem("skipLesson") === "true";
    return skipLessonStored ? workboards.ASSIGNMENT : workboards.LESSON;
  });

  /** Keeps the workboard name in sync with Lexical updates (React state lags one frame). */
  const editorActiveWorkboardNameRef = useRef(
    activeWorkboard?.name ?? "Lesson",
  );

  useEffect(() => {
    editorActiveWorkboardNameRef.current = activeWorkboard?.name ?? "Lesson";
  }, [activeWorkboard?.name]);

  const syncEditorActiveWorkboardName = useCallback((name) => {
    if (name) {
      editorActiveWorkboardNameRef.current = name;
    }
  }, []);
  useEffect(() => {
    setGenerateImageOpen(false)
  }, [activeWorkboard.name])

  /**
   * Story generation replaces the workboard switcher (UpdateActiveWorkbaordPlugin)
   * with the "Story Shell" label, so nothing ever points the editor at the Lesson
   * workboard the way clicking the Lesson tab does. Pin it here: without this the
   * Lexical sync writes the story shell into whichever workboard happened to be
   * active (Assignment when the `skipLesson` flag is stale), `workboards.LESSON.data`
   * stays empty, and update-lesson goes out with no `description`.
   */
  useEffect(() => {
    if (generationType !== GenerationType.STORY.value) return;

    sessionStorage.setItem("skipLesson", false);
    syncEditorActiveWorkboardName(workboards.LESSON.name);
    setActiveWorkboard(workboards.LESSON);
    setIsAssignment(false);
    setIsRubric(false);
    setPopupVisible(false);
    setActiveTab("lesson");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationType]);

  const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  useEffect(() => {
    showScrollButtonRef.current = showScrollButton;
  }, [showScrollButton]);

  useEffect(() => {
    if (activeWorkboard.name === "Assignment") {
      setWorkboardText(stripHtmlTags(workboards.ASSIGNMENT.data).trim());
    } else if (activeWorkboard.name === "Rubric") {
      setWorkboardText(stripHtmlTags(workboards.RUBRIC.data).trim());
    } else {
      setWorkboardText(stripHtmlTags(workboards.LESSON.data).trim());
    }
  }, [workboards]);

  useEffect(() => {
    let data;
    if (activeWorkboard.name === "Assignment") {
      data = workboards.ASSIGNMENT.data;
      if (Array.isArray(data)) data = data[assignmentsCountTwo] ?? "";
    } else if (activeWorkboard.name === "Rubric") {
      data = workboards.RUBRIC.data;
      if (Array.isArray(data)) data = data[rubricCounttTwo] ?? "";
    } else {
      data = workboards.LESSON.data;
    }

    // placeholder marks "never generated", not content
    if (data === "@$%^231#^#a5fgd%532^&4R3&") data = "";

    setIsWorkboardEmpty(!hasWorkboardContent(data));
  }, [workboards, activeWorkboard, assignmentsCountTwo, rubricCounttTwo]);

  function getAssignmentChatQuery(generateAllMode) {
    if (
      sessionStorage.getItem("skipLesson") === "true" &&
      (activeWorkboard.name === "Assignment" ||
        activeWorkboard.name === "Rubric")
    ) {
      setAssignmentgeneration(true);
    }

    if (
      sessionStorage.getItem("skipLesson") === "true" &&
      activeWorkboard.name === "Lesson"
    ) {
      sessionStorage.setItem("skipLesson", false);
    }

    if (SkipLessonflag && activeTab === "lesson") {
      sessionStorage.setItem("DisableLessonTab", false);
    } else {
      sessionStorage.setItem("DisableLessonTab", true);
    }
    setPopupVisible(false);
    const { name, start_date, end_date, duration_minutes, type } =
      formAssignmentState;
    const prompt_message = formState?.chat_query;
    let endDateText = end_date ? `Assignment End Date: ${end_date}` : "";
    if (generateAllMode) {
      return `Assignment Name: ${formState.name} 
      Start Date: ${start_date} 
      ${endDateText}  
      Assignment Timer: 60 mins
      Choose Assignment type: Mixed Questions  

      Create a Assignment`;
    } else {
      return `Assignment Name: ${name} 
      Start Date: ${start_date} 
      ${endDateText}  
      Assignment Timer: ${duration_minutes} mins
      Choose Assignment type: ${getAssignmentTypeName(type)}  

      ${prompt_message}`;
    }
  }

  const fileInputRef = useRef(null);
  const sendFiles = async () => {
    const files = await Promise.all(
      [...uploadedFileLink].map((file) => ({
        original_file_name: file.name,
        link: file.location,
        file_id: file.file_id,
      })),
    );
    // console.log("[send] uploadedFileLink:", uploadedFileLink);
    const collectionNames = [...uploadedFileLink]
      .map((f) => f.collection_name)
      .filter(Boolean);
    // console.log("[send] collectionNames:", collectionNames);

    const selectedRepoFileUrls = [...selectedRepoFiles]
      .map(
        (file) =>
          (typeof file === "string" ? file.trim() : file?.s3_url) || null,
      )
      .filter(Boolean);

    const payload = {
      room_id,
      lesson_name: formState.name,
      course_id: course?.course_id,
      query: isAssignment
        ? getAssignmentChatQuery()
        : isRubric
          ? `${formState?.chat_query}`
          : formState.chat_query,
      task_name: activeWorkboard.name,
      files,
      file_url_lesson_direct: selectedRepoFileUrls,
      ...(collectionNames.length > 0 && { collection_name: collectionNames }),
      ...(youtubeLinks?.[0]?.rawUrl && { yt_url: youtubeLinks[0].rawUrl }),
    };
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: `Token ${token}`,
    };
    try {
      const response = await api.post(`ai/api/upload/`, payload, headers);
      if (response.status === 200 || response.status === 201) {
      }
    } catch (error) {
      setSelectedFiles([]);
    }
  };

  /**
   * Returns true when generation actually started, false when it was rejected.
   *
   * The caller opens the left panel, and it used to do that before calling
   * here — so a missing lesson name still collapsed the workboard to half
   * width and revealed the prompt box, and only then said no. Everything that
   * can refuse the request is therefore checked up front, before any state is
   * set, and the verdict is handed back so the caller can wait for it.
   */
  const handleGenerateAll = (e) => {
    if (!formState.name) {
      toast.error("Please enter Lesson Name.", {
        position: "top-center",
      });
      return false;
    }

    // Also checked inside sendGenerateLession, which other callers still rely
    // on. It is repeated here because it has to be known before the layout
    // changes, not after.
    if (
      generationType === GenerationType.STORY.value &&
      !storySetup.storyDifficulty
    ) {
      toast.error("Please fill the difficulty details to generate.", {
        position: "top-center",
      });
      return false;
    }

    setGenerateAllMode(true);
    setActiveTab('lesson')
    generateAllPersistStartedRef.current = false;
    generateAllPersistCompletedRef.current = false;
    setGenerateAllRubricEditorSync(null);
    generateAllRefetchDoneRef.current = false;
    generateAllAssignmentIdRef.current = null;
    generateAllLessonSavePromiseRef.current = Promise.resolve();
    generateAllAssignmentSavePromiseRef.current = Promise.resolve();
    generateAllTaskNameRef.current = "Lesson";
    sendGenerateLession(e, true);
    return true;
  };

  function getAssignmentTypeName(value) {
    const entry = Object.values(AssignmentType).find((t) => t.value === value);
    return entry ? entry.name : null;
  }

  const sendGenerateLession = async (
    e,
    generateAllMode = false,
    generateAutoRubric = false,
  ) => {
    e.preventDefault();
    // TabToBottom();

    if (!formState.name) {
      toast.error("Please enter Lesson Name.", {
        position: "top-center",
      });
      return;
    }

    // Story generation is driven by the Story Setup panel, but only difficulty
    // is required — the learning objectives and the description are optional,
    // and are sent through empty when the teacher leaves them blank.
    if (generationType === GenerationType.STORY.value) {
      if (!storySetup.storyDifficulty) {
        toast.error("Please fill the difficulty details to generate.", {
          position: "top-center",
        });
        return;
      }
    }

    combinedMessageRef.current = [];

    if (isAssignment && !generateAllMode && !generateAutoRubric) {
      let errors = {};
      // if (!formAssignmentState?.name.trim()) {
      //   errors.name = "Name is required";
      // }
      if (!formAssignmentState?.name) {
        //change from above code to this one because of skip lesson
        errors.name = "Name is required";
      }

      if (isRequired(formAssignmentState?.duration_minutes)) {
        errors.duration_minutes = "Timer is required";
      }
      if (isRequired(formAssignmentState?.start_date)) {
        errors.start_date = "Start date is required";
      }
      if (isRequired(formAssignmentState?.type)) {
        errors.type = "Type is required";
      }

      setFormErrorAssignmentState(errors);

      if (Object.values(errors).some((error) => error)) {
        return;
      } else {
        setPopupVisible(false);
      }
    }

    const teacher_details = globalState.teacher_details?.data;
    const hist = chatHist.map((item) => {
      return {
        query: item.query,
        answer: Array(item.answer)
          ?.map((item) => item)
          .join(""),
      };
    });
    // if (socket && socket.readyState === WebSocket.OPEN) {

    //   // 👇 snapshot the current links; this copy is NOT wiped by setYoutubeLinks([])
    //   const ytSnapshot = [...youtubeLinks];
    //   youtubeLinksRef.current = ytSnapshot;

    //   const collectionNames = [...uploadedFileLink].map(f => f.collection_name).filter(Boolean);

    //   const generateAllTask =
    //     generateAllMode && generateAllTaskNameRef.current;

    //   /** Lower School story generation — drives the new fields and file exclusion. */
    //   const isStoryGeneration = generationType === GenerationType.STORY.value;

    //   let wsQuery;
    //   if (generateAllTask === "Lesson") {
    //     wsQuery = `Create a lesson`;
    //   } else if (generateAllTask === "Assignment") {
    //     wsQuery = getAssignmentChatQuery(generateAllMode);
    //   } else if (generateAllTask === "Rubric") {
    //     wsQuery = `create a rubric`;
    //   } else if (generateAutoRubric) {
    //     wsQuery = `create a rubric on ${getSpecificAssignmentType && getAssignmentTypeName(getSpecificAssignmentType)}`
    //   } else {
    //     wsQuery = isAssignment
    //       ? getAssignmentChatQuery(generateAllMode)
    //       : isRubric
    //         ? generateAllMode
    //           ? `create a rubric`
    //           : `${formState?.chat_query}`
    //         : generateAllMode
    //           ? `Create a lesson on ${formState.name}`
    //           : formState.chat_query;
    //   }

    //   let ytUrlToSend;

    //   if (activeWorkboard.name === "Lesson") {
    //     ytUrlToSend = ytSnapshot[0]?.rawUrl;
    //   } else if (activeWorkboard.name === "Assignment") {
    //     // prefer a freshly-attached link; fall back to the saved one
    //     // ytUrlToSend = ytSnapshot[0]?.rawUrl || getSavedYoutubeLinks || undefined;
    //     ytUrlToSend = ytSnapshot[0]?.rawUrl || undefined;
    //   }

    //   let taskName = generateAllMode && generateAllTaskNameRef.current
    //     ? generateAllTaskNameRef.current
    //     : activeWorkboard.name
    //   const repoFiles = taskName !== "Assignment" ? [...selectedRepoFiles, ...defaultRepoFiles]
    //     .map((file) => (typeof file === "string" ? file.trim() : file?.s3_url) || null)
    //     .filter(Boolean) : [...selectedRepoFiles]
    //       .map((file) => (typeof file === "string" ? file.trim() : file?.s3_url) || null)
    //       .filter(Boolean)

    //   const selectedRepoFileUrls = [...selectedRepoFiles]
    //     .map((file) => (typeof file === "string" ? file.trim() : file?.s3_url) || null)
    //     .filter(Boolean)

    //   let youtubePayload = {};

    //   if (ytUrlToSend) {
    //     youtubePayload = {
    //       yt_url: ytUrlToSend,
    //     };
    //   } else if (generateAllTask) {
    //     // Story generation carries no course content — course-default links included.
    //     if (generateAllTask !== "Assignment" && !isStoryGeneration) {
    //       youtubePayload = {
    //         yt_links: defaultYoutubeLink,
    //       };
    //     }
    //   } else if (!isAssignment && !isStoryGeneration) {
    //     youtubePayload = {
    //       yt_links: defaultYoutubeLink,
    //     };
    //   }

    //   let payload = {
    //     query: wsQuery,
    //     // : generateAllMode ? `Create a Assignment` : formState.chat_query,
    //     message: hist,
    //     // Story generation works off the objectives + description alone — no file context.
    //     file_url: isStoryGeneration ? [] : repoFiles,
    //     file_url_lesson_direct: isStoryGeneration ? [] : selectedRepoFileUrls,
    //     teacher_name: teacher_details?.name,
    //     chatbot_name: teacher_details?.ai_name,
    //     course_name: course?.name,
    //     subject: course?.subject,
    //     lesson_name: formState.name,
    //     // generation_type: generationType,
    //     generate_all_mode: generateAllMode,
    //     lower_school: isStoryGeneration,

    //     // Story Setup fields only apply to the story flow.
    //     ...(isStoryGeneration && {
    //       difficulty: storySetup.storyDifficulty,
    //       learning_objective: storySetup.selectedObjectives,
    //       // What the teacher sees in the textbox: streamed text plus any edits.
    //       learning_objective_description: storySetup.storyDescription,
    //     }),

    //     ...youtubePayload,

    //     task_name:
    //       generateAllMode && generateAllTaskNameRef.current
    //         ? generateAllTaskNameRef.current
    //         : generateAutoRubric ? "Rubric" : activeWorkboard.name,

    //     ...(collectionNames.length > 0 &&
    //       !isStoryGeneration && {
    //       collection_name: collectionNames,
    //     }),
    //     ...(generateImageOpen && {
    //       generate_img_only: generateImageOpen
    //     })
    //   };

    //   if (selectedFiles.length > 0) {
    //     sendFiles();
    //   }

    //   // Update chatHist with the new query

    //   let normalFiles =
    //     selectedFiles.length > 0
    //       ? selectedFiles.map((item) => ({
    //         original_name: item?.name,
    //       }))
    //       : [];

    //   let myRepoFiles =
    //     selectedRepoFiles.length > 0
    //       ? selectedRepoFiles.map(({ file_name, file_type }) => ({
    //         original_name: /\.[^/.]+$/.test(file_name)
    //           ? file_name
    //           : `${file_name}.${file_type}`,
    //       }))
    //       : [];

    //   let MyLink =
    //     ytSnapshot.length > 0
    //       ? [{
    //         title: ytSnapshot[0]?.video_title || "YouTube Link", type: "yt_link", source: "yt_link", url: ytSnapshot[0]?.rawUrl || "",
    //       }]
    //       : [];

    //   setChatHist((prevChatHist) => [
    //     ...prevChatHist,
    //     {
    //       query: formState?.chat_query,
    //       answer: "",
    //       file: [...normalFiles, ...myRepoFiles, ...MyLink],
    //     },
    //   ]);

    //   setTimeout(() => {
    //     TabToBottom();
    //   }, 100);

    //   const payloadJSON = JSON.stringify(payload);
    //   //  console.log("WebSocket payload:", payloadJSON); // Log the payload before sending
    //   if (selectedFiles.length === 0) {
    //     // ✅ new generation = fresh scroll state
    //     setShowScrollButton(false);
    //     showScrollButtonRef.current = false;

    //     setGanrateLesson(true);
    //     socket.send(payloadJSON);
    //   }
    //   // Send the JSON data over the WebSocket connection

    //   setFormState((prev) => ({ ...prev, chat_query: "" }));
    // }
    // Add functionality to enhance the description here

    // Reconnect on-demand if the socket dropped (e.g. Generate by AI clicked after idle timeout)
    let activeSocket = socket;
    if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
      try {
        activeSocket = await ensureSocketConnected();
      } catch (err) {
        console.error("Socket reconnect failed:", err);
        // toast.error("Connection lost. Please try again.", {
        //   position: "top-center",
        // });
        return;
      }
    }

    {
      // 👇 snapshot the current links; this copy is NOT wiped by setYoutubeLinks([])
      const ytSnapshot = [...youtubeLinks];
      youtubeLinksRef.current = ytSnapshot;

      const collectionNames = [...uploadedFileLink]
        .map((f) => f.collection_name)
        .filter(Boolean);

      const generateAllTask = generateAllMode && generateAllTaskNameRef.current;

      /** Lower School story generation — drives the new fields and file exclusion. */
      const isStoryGeneration = generationType === GenerationType.STORY.value;

      let wsQuery;
      if (generateAllTask === "Lesson") {
        wsQuery = `Create a lesson`;
      } else if (generateAllTask === "Assignment") {
        wsQuery = getAssignmentChatQuery(generateAllMode);
      } else if (generateAllTask === "Rubric") {
        wsQuery = `create a rubric`;
      } else if (generateAutoRubric) {
        wsQuery = `create a rubric on ${getSpecificAssignmentType && getAssignmentTypeName(getSpecificAssignmentType)}`;
      } else {
        wsQuery = isAssignment
          ? getAssignmentChatQuery(generateAllMode)
          : isRubric
            ? generateAllMode
              ? `create a rubric`
              : `${formState?.chat_query}`
            : generateAllMode
              ? `Create a lesson on ${formState.name}`
              : formState.chat_query;
      }

      let ytUrlToSend;

      if (activeWorkboard.name === "Lesson") {
        ytUrlToSend = ytSnapshot[0]?.rawUrl;
      } else if (activeWorkboard.name === "Assignment") {
        // prefer a freshly-attached link; fall back to the saved one
        // ytUrlToSend = ytSnapshot[0]?.rawUrl || getSavedYoutubeLinks || undefined;
        ytUrlToSend = ytSnapshot[0]?.rawUrl || undefined;
      }

      let taskName =
        generateAllMode && generateAllTaskNameRef.current
          ? generateAllTaskNameRef.current
          : activeWorkboard.name;
      const repoFiles =
        taskName !== "Assignment"
          ? [...selectedRepoFiles, ...defaultRepoFiles]
            .map(
              (file) =>
                (typeof file === "string" ? file.trim() : file?.s3_url) ||
                null,
            )
            .filter(Boolean)
          : [...selectedRepoFiles]
            .map(
              (file) =>
                (typeof file === "string" ? file.trim() : file?.s3_url) ||
                null,
            )
            .filter(Boolean);

      const selectedRepoFileUrls = [...selectedRepoFiles]
        .map(
          (file) =>
            (typeof file === "string" ? file.trim() : file?.s3_url) || null,
        )
        .filter(Boolean);

      let youtubePayload = {};

      if (ytUrlToSend || defaultYoutubeLink.length > 0) {
        youtubePayload = {
          yt_url: ytUrlToSend,
          yt_links: defaultYoutubeLink,
        };
      } else if (activeWorkboard.name === "Assignment") {
        youtubePayload = {
          ...(ytUrlToSend && { yt_url: ytUrlToSend }),
          ...(defaultYoutubeLink && { yt_links: defaultYoutubeLink }),
        };
      }

      else if (generateAllMode) {
        // Generate-all: Lesson + Assignment steps get the course-default links, Rubric gets none.
        // Story generation carries no course content — course-default links included.
        if (generateAllTaskNameRef.current !== "Rubric" && !isStoryGeneration) {
          youtubePayload = {
            yt_links: defaultYoutubeLink,
          };
        }
      } else if (!isAssignment && !isStoryGeneration) {
        youtubePayload = {
          yt_links: defaultYoutubeLink,
        };
      }




      let payload = {
        query: wsQuery,
        // : generateAllMode ? `Create a Assignment` : formState.chat_query,
        message: hist,
        // Story generation works off the objectives + description alone — no file context.
        file_url: isStoryGeneration ? [] : repoFiles,
        file_url_lesson_direct: isStoryGeneration ? [] : selectedRepoFileUrls,
        teacher_name: teacher_details?.name,
        chatbot_name: teacher_details?.ai_name,
        course_name: course?.name,
        subject: course?.subject,
        lesson_name: formState.name,
        // generation_type: generationType,
        generate_all_mode: generateAllMode,
        lower_school: isStoryGeneration,

        // Story Setup fields only apply to the story flow.
        ...(isStoryGeneration && {
          course_id: course?.course_id ?? null,
          difficulty: storySetup.storyDifficulty,
          learning_objective: storySetup.selectedObjectives,
          // What the teacher sees in the textbox: streamed text plus any edits.
          learning_objective_description: storySetup.storyDescription,
        }),

        ...youtubePayload,

        task_name:
          generateAllMode && generateAllTaskNameRef.current
            ? generateAllTaskNameRef.current
            : generateAutoRubric
              ? "Rubric"
              : activeWorkboard.name,

        ...(collectionNames.length > 0 &&
          !isStoryGeneration && {
          collection_name: collectionNames,
        }),
        ...(generateImageOpen && {
          generate_img_only: generateImageOpen,
        }),
      };

      if (selectedFiles.length > 0) {
        sendFiles();
      }

      // Update chatHist with the new query

      let normalFiles =
        selectedFiles.length > 0
          ? selectedFiles.map((item) => ({
            original_name: item?.name,
          }))
          : [];

      let myRepoFiles =
        selectedRepoFiles.length > 0
          ? selectedRepoFiles.map(({ file_name, file_type }) => ({
            original_name: /\.[^/.]+$/.test(file_name)
              ? file_name
              : `${file_name}.${file_type}`,
          }))
          : [];

      let MyLink =
        ytSnapshot.length > 0
          ? [
            {
              title: ytSnapshot[0]?.video_title || "YouTube Link",
              type: "yt_link",
              source: "yt_link",
              url: ytSnapshot[0]?.rawUrl || "",
            },
          ]
          : [];

      setChatHist((prevChatHist) => [
        ...prevChatHist,
        {
          query: formState?.chat_query,
          answer: "",
          file: [...normalFiles, ...myRepoFiles, ...MyLink],
        },
      ]);

      setTimeout(() => {
        TabToBottom();
      }, 100);

      const payloadJSON = JSON.stringify(payload);
      //  console.log("WebSocket payload:", payloadJSON); // Log the payload before sending
      if (selectedFiles.length === 0) {
        // ✅ new generation = fresh scroll state
        setShowScrollButton(false);
        showScrollButtonRef.current = false;

        setGanrateLesson(true);
        activeSocket.send(payloadJSON);
      }
      // Send the JSON data over the WebSocket connection

      setFormState((prev) => ({ ...prev, chat_query: "" }));
    }

    setSelectedFiles([]);
    setSelectedRepoFiles([]);
    setYoutubeLinks([]);
    setprogressTracker([]);
  };

  const handleAssignmentInputChange = (event) => {
    const { name, value } = event.target;
    let errors = {};
    if (name === "name") {
      // Check if the value is empty or just spaces
      if (!value.trim()) {
        errors.name = "This field is required";
      } else {
        // If it's not empty, apply the isRequired validation
        errors.name = isRequired(value);
      }
    }
    if (name === "assignment") {
      errors.assignment = isRequired(value);
    }
    if (name === "duration_minutes") {
      errors.duration_minutes = isRequired(value);
    }
    if (name === "start_date") {
      errors.start_date = isRequired(value);
    }
    if (name === "end_date") {
      errors.end_date = isRequired(value);
    }
    if (name === "type") {
      errors.type = isRequired(value);
    }
    setFormErrorAssignmentState(errors);

    setFormAssignmentState((prev) => ({ ...prev, [name]: value }));
  };
  const assignmentDurationChangeTime = (value) => {
    setAssignmentDuration((prevTime) => {
      let { minutes } = prevTime;

      // Increment/Decrement minutes
      minutes += value;

      // Limit the minutes to the range 0-180
      minutes = Math.max(0, Math.min(180, minutes));

      // Update the input value for display
      const newInputValue = minutes;
      setFormAssignmentState((prev) => ({
        ...prev,
        duration_minutes: newInputValue,
      }));

      return { minutes };
    });
  };

  const [assignmentDurationCount, setAssignmentDurationCount] = useState(
    Number(sessionStorage.getItem("assignmentDurationCount")),
  );

  useEffect(() => {
    sessionStorage.setItem("assignmentDurationCount", assignmentDurationCount);

    return () => {
      sessionStorage.setItem("assignmentDurationCount", 0);
    };
  }, [assignmentDurationCount]);

  const handleAssignmentInputChangedate = (name, date) => {
    let errors = {};
    if (name === "start_date") {
      errors.start_date = isRequired(format(new Date(date), "yyyy-MM-dd"));
    }
    setFormErrorAssignmentState(errors);
    setFormAssignmentState((prev) => ({
      ...prev,
      [name]: format(new Date(date), "yyyy-MM-dd"), // Use the provided name
    }));
  };

  const showUploadPopupcall = () => {
    if (showUploadPop) {
      setShowUploadPop(false);
    } else {
      setShowUploadPop(true);
    }
  };
  // Dictated text continues the prompt's sentence unless the prompt ends one
  const startsNewSentence = (base) => !base || /[.?!]\s*$/.test(base);

  // Commit a finished dictation session into the prompt
  const appendDictation = (spokenText) => {
    setFormState((prevState) => {
      const base = (prevState.chat_query || "").replace(/\s+$/, "");
      const spoken = endSentence(
        formatDictation(spokenText, { capitalizeStart: startsNewSentence(base) }),
      );
      if (!spoken) return prevState;
      return { ...prevState, chat_query: base ? `${base} ${spoken}` : spoken };
    });
  };

  const {
    isListening,
    transcript: liveDictation,
    startListening,
    stopListening,
    browserSupportsSpeechRecognition,
  } = useDictation({
    // Recognition language follows the course (e.g. "hi", "fr", "es")
    language: courseLanguage,
    onFinal: appendDictation,
  });

  // Live preview shown after the prompt while speaking (LessonGenerator joins them)
  const transcript = formatDictation(liveDictation, {
    capitalizeStart: startsNewSentence(formState.chat_query),
  });

  const startStopListening = () => {
    if (isListening) {
      stopVoiceInput();
    } else {
      startListening();
    }
  };
  // onFinal appends the text to chat_query
  const stopVoiceInput = () => {
    stopListening();
  };

  // Save form data to localStorage whenever formState changes
  const handleInputKeyPress = (event) => {
    const { name, value } = event.target;

    // Handle input changes (for onChange event)
    if (event.type === "change" || value !== "" || value !== 0) {
      setFormState((prev) => ({ ...prev, [name]: value }));
      setFormState((prev) => ({ ...prev, chat_hist: chatHist }));
    }

    // Handle Enter key press (for onKeyDown event)
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();

      if (isLockedBySubmissions) {
        toast.error(SUBMISSION_LOCKED_MESSAGE, { position: "top-center" });
        return;
      }

      if (value !== "" && value !== 0) {
        // Condition 1: Check if name is provided.
        const isNameProvided = formState.name;

        // Condition 2: If chat_query exists, ensure it's not null or an empty string.
        const isChatQueryValid = formState.chat_query
          ? formState.chat_query !== null && formState.chat_query !== ""
          : true;

        // Condition 3: If selectedFiles exist, ensure they correspond to progressTracker with all statuses 'Completed'.
        const isSelectedFilesValid = selectedFiles.length
          ? selectedFiles.length === progressTracker.length &&
          progressTracker.every((obj) => obj.status === "Completed")
          : true;

        if (!isNameProvided) {
          toast.error("Please enter Lesson Name.", {
            position: "top-center",
          });
        }

        if (
          isNameProvided &&
          isChatQueryValid &&
          isSelectedFilesValid &&
          !reFormatterLoading &&
          !generateAllMode &&
          !ganrateLesson
        ) {
          sendGenerateLession(event);
        }
      }
    }
  };
  const [previewDtls, setpreviewDtls] = useState([]);
  const [openStudentView, setopenStudentView] = useState(false);




  // ENDOFTURN means the backend has finished the images too — fetch the history
  // and swap every placeholder for its real <img>.
  useEffect(() => {
    const wasGenerating = prevGanrateForImagesRef.current === true;
    prevGanrateForImagesRef.current = ganrateLesson;
    if (!(wasGenerating && ganrateLesson === false)) return;

    const hist = chatHistRef.current;
    const idx = hist.length - 1;
    const turn = hist[idx];
    if (!turn?.answer || !hasPendingImages(turn.answer)) return;

    const turnUuid = turn.uuid;
    const key = turnUuid ?? `idx-${idx}`;
    if (handledImageTurnsRef.current.has(key)) return;

    const lessonPk = lessonIdRef.current;
    if (!lessonPk) {
      // No lesson row yet — nothing to query against. Resolve immediately so the
      // Generate All persist chain isn't left waiting on a call we can't make.
      lessonImagesPromiseRef.current = Promise.resolve(null);
      return;
    }

    handledImageTurnsRef.current.add(key);

    // Stable per turn so a retry after a network failure reuses the same key.
    if (!turnIdemKeysRef.current.has(key)) {
      turnIdemKeysRef.current.set(key, makeIdempotencyKey());
    }
    const idemKey = turnIdemKeysRef.current.get(key);

    const streamed = turn.answer;
    setIsResolvingImages(true);

    const p = api
      .post(
        "ai/api/lesson_chat_history/",
        { lesson_id: Number(lessonPk), x_idempotency_key: idemKey },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      )
      .then((res) => {
        const merged = resolveAnswerFromHistories(
          streamed,
          res?.data?.data?.histories,
          turnUuid,
        );
        if (!merged || merged === streamed) return merged;

        setChatHist((prev) => {
          const next = [...prev];
          const target =
            turnUuid != null ? next.findIndex((c) => c?.uuid === turnUuid) : idx;
          if (target < 0 || !next[target]) return prev;
          next[target] = { ...next[target], answer: merged };
          return next;
        });
        return merged;
      })
      .catch((err) => {
        console.error("lesson image resolve failed:", err);
        // allow a retry on a later turn; step 4 strips placeholders before any save
        handledImageTurnsRef.current.delete(key);
        return null;
      })
      .finally(() => setIsResolvingImages(false));

    lessonImagesPromiseRef.current = p;
  }, [ganrateLesson, token]);



  const showstudentviewDtls = () => {
    setpreviewDtls(
      isAssignment
        ? removeCloseImageSpans(workboards.ASSIGNMENT.data)
        : isRubric
          ? removeCloseImageSpans(workboards.RUBRIC.data)
          : removeCloseImageSpans(
            workboards.LESSON.data === "@$%^231#^#a5fgd%532^&4R3&"
              ? ""
              : workboards.LESSON.data,
          ),
    );

    setopenStudentView(!openStudentView);
  };

  const updateDefaultWorkboardData = (name, data) => {
    setDefaultWorkboardData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        data,
      },
    }));
  };

  // Coerce a rubric value (HTML string or { data, Id }) into { data, Id }, keeping the previous Id if none is given
  const toRubricEntry = (value, prevEntry) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return {
        data: value.data ?? "",
        Id: value.Id ?? prevEntry?.Id ?? null,
      };
    }
    return { data: value ?? "", Id: prevEntry?.Id ?? null };
  };

  const updateGlobleSaveData = (name, newData, formAssignmentState) => {
    setGlobleSaveData((prevWorkboards) => {
      // Find the key of the workboard we want to update
      const workboardKey = Object.keys(prevWorkboards).find(
        (key) => prevWorkboards[key].name === name,
      );
      // If we found a workboard with the provided name

      if (workboardKey) {
        let updatedData;
        let updatedFormAssignment;

        if (isAssignment) {
          // Create a copy of the existing data array
          updatedData = Array.isArray(prevWorkboards[workboardKey].data)
            ? [...prevWorkboards[workboardKey].data]
            : [];
          updatedData[assignmentsCountTwo] = newData;
        } else if (workboardKey === "RUBRIC") {
          // RUBRIC.data must always be [{ data, Id }]; normalize existing slots too
          const prevData = prevWorkboards[workboardKey].data;
          updatedData = (Array.isArray(prevData) ? prevData : []).map((entry) =>
            toRubricEntry(entry),
          );
          updatedData[rubricCounttTwo] = toRubricEntry(
            newData,
            updatedData[rubricCounttTwo],
          );
        } else {
          updatedData = newData;
        }

        const updatedWorkboards = {
          ...prevWorkboards, // Copy all the existing workboards
          [workboardKey]: {
            ...prevWorkboards[workboardKey], // Copy all the existing properties of the workboard
            data: updatedData, // Update the data property with the new array
          },
        };

        // Store updated state in localStorage
        return updatedWorkboards;
      } else {
        // If no workboard was found with the given name, return the previous state
        return prevWorkboards;
      }
    });
  };

  /** Keeps `workboards` in sync with API responses. Pass `slotIndex` for Assignment/Rubric list slots. */
  const updateWorkboardDataByName = (name, newData, slotIndex) => {
    if (newData == null) return;
    const cleaned =
      typeof newData === "string"
        ? removeCloseImageSpans(newData)
        : removeCloseImageSpans(String(newData));

    setWorkboards((prevWorkboards) => {
      const workboardKey = Object.keys(prevWorkboards).find(
        (key) => prevWorkboards[key].name === name,
      );
      if (!workboardKey) return prevWorkboards;

      let nextData = cleaned;

      if (workboardKey === "ASSIGNMENT" && typeof slotIndex === "number") {
        const prevData = prevWorkboards[workboardKey].data;
        const arr = Array.isArray(prevData)
          ? [...prevData]
          : prevData === "" || prevData == null
            ? []
            : [prevData];
        while (arr.length <= slotIndex) arr.push("");
        arr[slotIndex] = cleaned;
        nextData = arr;
      } else if (workboardKey === "RUBRIC" && typeof slotIndex === "number") {
        const prevData = prevWorkboards[workboardKey].data;
        const arr = Array.isArray(prevData)
          ? [...prevData]
          : prevData === "" || prevData == null
            ? []
            : [prevData];
        while (arr.length <= slotIndex) arr.push("");
        arr[slotIndex] = cleaned;
        nextData = arr;
      }

      return {
        ...prevWorkboards,
        [workboardKey]: {
          ...prevWorkboards[workboardKey],
          data: nextData,
        },
      };
    });
  };

  const updateGlobleOnChangeData = (
    name,
    newData,
    assingmentpopUp,
    assingmentpopUpData,
    newDataForOnlySave,
  ) => {
    let globleOnChangeData = JSON.parse(
      sessionStorage.getItem("globleOnChangeData"),
    );
    // Ensure ASSIGNMENT.data is always an array after reading
    if (
      globleOnChangeData &&
      globleOnChangeData.ASSIGNMENT &&
      !Array.isArray(globleOnChangeData.ASSIGNMENT.data)
    ) {
      globleOnChangeData.ASSIGNMENT.data = [globleOnChangeData.ASSIGNMENT.data];
    }

    setGlobleOnChange((prevWorkboards) => {
      // Find the key of the workboard we want to update
      const workboardKey = Object.keys(prevWorkboards).find(
        (key) => prevWorkboards[key].name === name,
      );
      // If we found a workboard with the provided name
      if (workboardKey) {
        let updatedData;
        let updatedFormAssignment;

        if (workboardKey === "ASSIGNMENT") {
          // Always treat as array
          updatedData = Array.isArray(prevWorkboards[workboardKey].data)
            ? [...prevWorkboards[workboardKey].data]
            : [];
          updatedData[Number(assignmentsCountTwo)] = newData;
        } else if (workboardKey === "RUBRIC") {
          updatedData = Array.isArray(prevWorkboards[workboardKey].data)
            ? [...prevWorkboards[workboardKey].data]
            : [];
          updatedData[rubricCounttTwo] = newData;
        } else {
          updatedData = newData;
        }

        // Ensure updatedData is always an array for ASSIGNMENT
        if (workboardKey === "ASSIGNMENT" && !Array.isArray(updatedData)) {
          updatedData = [updatedData];
        }

        const updatedWorkboards = {
          ...prevWorkboards,
          [workboardKey]: {
            ...prevWorkboards[workboardKey],
            data: updatedData,
          },
        };
        return updatedWorkboards;
      } else {
        // If no workboard was found with the given name, return the previous state
        return prevWorkboards;
      }
    });
  };

  const { handleAssinmentRubricSubmit } = useAssignmentRubricSubmit({
    lowerSchoolPayload: getLowerSchoolPayload(),
    formState,
    formAssignmentState,
    activeWorkboard,
    activeTab,
    chatHist,
    teacher_id,
    token,
    lesson_id,
    rubric_id,
    assignment_id,
    previesAssignment,
    previesRubric,
    eachAssignment_id,
    eachRubric_id,
    workboards,
    defaultWorkboardData,
    globleSaveData,
    course,
    room_id,
    SkipLessonflag,
    getassignmentdetails,
    assignmentsCount,
    assignmentsCountTwo,
    rubricCounttTwo,
    isAssignment,
    isRubric,
    setIstoggleClick,
    setGlobleOnChangeDataSave,
    setClickSaveBTN,
    setClickSaveRubricBTN,
    setAssignmentgeneration,
    setLoading,
    setShowCourseSuccessfully,
    setLoadingPublishSchoology,
    updateDefaultWorkboardData,
    updateWorkboardDataByName,
    updateGlobleSaveData,
    setLesson,
    setPreviesAssignment,
    setPreviesRubric,
    setEachAssignment_id,
    setEachRubric_id,
    setPopupVisible,
    setFormState,
    setassignment_id,
    setrubric_id,
    setlesson_id,
    setAssingmentListOfID,
    setGlobleSaveData,
    setFormErrorAssignmentState,
    isRequired,
    checkAssignmentopen,
    isSaveChecker,
    setisSaveChecker,
    getLessons,
    getCourseDetails,
    params,
    currentHideTimerState,
    setHideTimerStates,
    generateAllMode,
    setCallSwitchToRubricFun,
    setIsAssignment,
    setIsRubric,
    setActiveWorkboard,
    setPendingAssignmentClickArgs,
    setskipLessonSession,
    skipLessonSession,
    setIsSaveLoading,
    setActiveTab,
    setGetSavedYoutubeLinks,
    createManuallyMode,
    stopGenerateAllMode,
    lesson,
    isStoryGeneration: generationType === GenerationType.STORY.value,
    setIsLessonSaved,
    setTypeChanged,
    setGetSpecificAssignmentType,
    getSpecificAssignmentType,
    setFormAssignmentState,
    globalState,
  });

  const updateLesson = async (id, updatedData, options, taskName) => {
    setToggleLoader(taskName);

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      };

      const endpoint =
        options === "assignment"
          ? `assignments/update-assignment/${id}/`
          : `lessons/update-lesson/${id}/`;

      const response = await api.put(endpoint, updatedData, { headers });
      // console.log("Updated Successfully:", response.data);

      if (response.data.status === 200) {
        setToggleLoader(null);
        return response.data;
      }
    } catch (error) {
      setToggleLoader(null);
      // console.error("API Error:", error);
      toast.error(error.response?.data?.message || "Failed to update lesson");
    }
  };

  const getSectionData = (section) => {
    if (!section) return null;

    // CASE 1
    if (typeof section.data === "string") {
      return removeCloseImageSpans(section.data);
    }

    // CASE 2
    if (section.data?.data?.data) {
      return removeCloseImageSpans(section.data.data.data);
    }

    return null;
  };

  const cleanHtml = (html = "") => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // remove ALL pre tags (nested or single)
    doc.querySelectorAll("pre").forEach((el) => {
      el.remove();
    });

    // remove empty paragraphs
    doc.querySelectorAll("p").forEach((p) => {
      const content = p.innerHTML.trim();

      if (content === "" || content === "<br>") {
        p.remove();
      }
    });

    // remove dir attributes
    doc.querySelectorAll("[dir]").forEach((el) => {
      el.removeAttribute("dir");
    });

    // remove inline styles
    doc.querySelectorAll("[style]").forEach((el) => {
      el.removeAttribute("style");
    });

    // remove ONLY math-node class
    doc.querySelectorAll(".math-node").forEach((el) => {
      el.classList.remove("math-node");

      // remove empty class=""
      if (el.classList.length === 0) {
        el.removeAttribute("class");
      }
    });

    // collapse extra whitespace between tags/text
    return doc.body.innerHTML.replace(/\s+/g, " ").trim();
  };

  const getArraySectionData = (section) => {
    if (!Array.isArray(section?.data)) return [];

    return section.data.map((item) => {
      const html = typeof item === "string" ? item : item?.data || "";

      return cleanHtml(removeCloseImageSpans(html));
    });
  };

  const [switchschoologySync, setswitchschoologySync] = useState(false);

  const handleSwitchToggle = () => {
    const newIsSwitchOn = !isSwitchOn; // get toggled value
    const payload = {
      is_personalize: newIsSwitchOn,
    };
    updateLesson(lesson_id, payload, "lesson", "Personalize");
    setIsSwitchOn(newIsSwitchOn);
  };

  const registerGenerateAllRefetchCallbacks = useCallback((callbacks) => {
    if (!callbacks) {
      generateAllRefetchAfterAssignmentRef.current = null;
      generateAllRefetchAfterRubricRef.current = null;
      return;
    }
    generateAllRefetchAfterAssignmentRef.current =
      callbacks.afterCreateAssignment ?? null;
    generateAllRefetchAfterRubricRef.current =
      callbacks.afterCreateRubric ?? null;
  }, []);

  const persistGenerateAllLessonApi = async (lessonHtml) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Token ${token}`,
    };
    const roomID = sessionStorage.getItem("roomId");
    const SL = sessionStorage.getItem("skipLesson");
    const fs = formStateRef.current;
    const ch = chatHistRef.current;

    let processedHtml = lessonHtml;
    try {
      processedHtml = await convertToLexicalHtml(lessonHtml);
    } catch (err) {
      // console.error("convertToLexicalHtml failed, using raw html:", err);
    }
    const description = removeCloseImageSpans(processedHtml);

    let lessonPayload;
    if (SL === "true") {
      lessonPayload = {
        name: fs?.name ?? "",
        start_date: fs?.start_date ?? "",
        end_date: fs?.end_date,
        description: null,
        course: course?.course_id ?? "",
        teacher: teacher_id,
        status: 3,
        user_query: { question: ch[0]?.query ?? "" },
        ai_answer: { answer: ch[0]?.answer ?? "" },
        room_id: roomID,
        is_personalize: isSwitchOn,
        is_file_download: isSwitchOn2,
        lesson_preview: isSwitchOnLessonPreview,
        virtual_assistant: isSwitchOnVirtualAssistant,
        is_skip: true,
        ...getLowerSchoolPayload(),
      };
    } else {
      lessonPayload = {
        name: fs?.name ?? "",
        start_date: fs?.start_date ?? "",
        end_date: fs?.end_date,
        description,
        course: course?.course_id ?? "",
        teacher: teacher_id,
        status: 3,
        user_query: { question: ch[0]?.query ?? "" },
        ai_answer: { answer: ch[0]?.answer ?? "" },
        room_id: roomID,
        is_personalize: isSwitchOn,
        is_file_download: isSwitchOn2,
        lesson_preview: isSwitchOnLessonPreview,
        virtual_assistant: isSwitchOnVirtualAssistant,
        ...getLowerSchoolPayload(),
      };
    }

    const lid = lessonIdRef.current;
    const lessonUrl =
      lid !== "" ? `lessons/update-lesson/${lid}/` : "lessons/create-lesson/";
    const lessonRes =
      lid !== ""
        ? await api.put(lessonUrl, lessonPayload, { headers })
        : await api.post(lessonUrl, lessonPayload, { headers });

    const newLessonId = lessonRes?.data?.data?.lesson_id ?? lid;
    if (lid === "") {
      setlesson_id(newLessonId);
    }
    lessonIdRef.current = newLessonId;

    const lessonDesc = lessonRes?.data?.data?.description ?? "";
    const lessonData = { Id: newLessonId, data: lessonDesc };
    sessionStorage.setItem("lessonDetails", JSON.stringify(lessonData));
    updateDefaultWorkboardData(defaultWorkboardData.LESSON.name, lessonDesc);
    setLesson(lessonData);
    setGlobleSaveData((prev) => ({
      ...prev,
      LESSON: { ...prev.LESSON, data: lessonDesc },
    }));

    if (lessonDesc != null && lessonDesc !== "") {
      updateWorkboardDataByName("Lesson", lessonDesc);
    }

    // Defer preview refetch during Generate All — same-origin deploys share a small
    // HTTP/1.1 connection pool; extra GETs here can stall create-rubric.
    if (!generateAllMode) {
      try {
        const lessonList = await getLessons();
        const courseDetails = await getCourseDetails();
        sessionStorage.setItem(
          "previewLessonData",
          JSON.stringify({
            lesson: lessonList,
            course: courseDetails,
            teacher_id: params.get("teacher_id"),
          }),
        );
      } catch {
        // ignore
      }
    }
  };

  const persistGenerateAllAssignmentApi = async (assignmentHtml) => {
    let lessonPk = lessonIdRef.current;
    if (!lessonPk) {
      try {
        await generateAllLessonSavePromiseRef.current;
      } catch {
        // parallel lesson save may have failed; toasts handled in that path
      }
      lessonPk = lessonIdRef.current;
    }
    if (!lessonPk) {
      throw new Error(
        "Generate All: missing lesson id before assignment save.",
      );
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Token ${token}`,
    };
    const fs = formStateRef.current;
    const fas = formAssignmentStateRef.current;

    let processedHtml = assignmentHtml;
    try {
      processedHtml = await convertToLexicalHtml(assignmentHtml);
    } catch (err) {
      // console.error("convertToLexicalHtml failed, using raw html:", err);
    }

    const assignmentPayload = {
      name: `${fs?.name ?? ""}`,
      assignment: removeCloseImageSpans(processedHtml),
      lesson: lessonPk,
      duration_minutes: 60,
      start_date: fas?.start_date,
      end_date: fas?.end_date,
      type: 7,
      synced_with_schoology: switchschoologySync,
      hide_timer: currentHideTimerState,
    };

    const assignRes = await api.post(
      "assignments/create-assignment/",
      assignmentPayload,
      { headers },
    );
    const newAssignId = assignRes?.data?.data?.assignment_id;
    if (newAssignId != null) {
      setassignment_id(newAssignId);
      setEachAssignment_id(newAssignId);
      generateAllAssignmentIdRef.current = newAssignId;
    }

    // Refetch is deferred until after create-rubric in flushGenerateAllPersist so
    // concurrent GETs do not exhaust the browser connection pool on deployed hosts.
    if (!generateAllMode) {
      try {
        await generateAllRefetchAfterAssignmentRef.current?.(lessonPk);
      } catch (refetchErr) {
        // console.error("Generate All refetch after create-assignment:", refetchErr);
      }
    }

    const assignmentFromApi = assignRes?.data?.data?.assignment;
    if (assignmentFromApi != null) {
      updateWorkboardDataByName(
        "Assignment",
        assignmentFromApi,
        assignmentsCountTwoRef.current,
      );
    }
  };

  /** Rubric only — lesson + assignment already persisted during WS orchestration. */
  const flushGenerateAllPersist = async () => {
    if (generateAllPersistCompletedRef.current) return;
    if (generateAllPersistStartedRef.current) return;

    const hist = chatHistRef.current;

    let rawAnswer = hist[hist.length - 1]?.answer ?? "";
    try {
      rawAnswer = await convertToLexicalHtml(
        hist[hist.length - 1]?.answer ?? "",
      );
    } catch (err) {
      // console.error("convertToLexicalHtml failed, using raw html:", err);
    }
    if (!rawAnswer) return;

    generateAllPersistStartedRef.current = true;
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      };

      const html = markdownAnswerToHtml(rawAnswer);

      // Ensure lesson + assignment saves finish before opening another connection.
      try {
        await generateAllLessonSavePromiseRef.current;
      } catch {
        // lesson save may have failed; create-rubric still attempted below
      }
      try {
        await generateAllAssignmentSavePromiseRef.current;
      } catch {
        // assignment save may have failed
      }

      const lessonPk = lessonIdRef.current;
      const assignPk = generateAllAssignmentIdRef.current ?? assignment_id;

      const rubricPayload = {
        rubric: removeCloseImageSpans(html),
        lesson: lessonPk,
        synced_with_schoology: switchschoologySync,
        ...(assignPk && { assignment: assignPk }),
      };

      const rubricRes = await api.post(
        "assignments/create-rubric/",
        rubricPayload,
        { headers },
      );
      const newRubricId = rubricRes?.data?.data?.rubric_id;
      if (newRubricId != null) {
        setrubric_id(newRubricId);
      }

      const rubricFromApi = rubricRes?.data?.data?.rubric;
      const rubricSlot = rubricCounttTwoRef.current;
      const rubricHtml = rubricFromApi ?? html;
      if (rubricFromApi != null) {
        updateWorkboardDataByName("Rubric", rubricFromApi, rubricSlot);
      }
      updateDefaultWorkboardData(defaultWorkboardData.RUBRIC.name, rubricHtml);
      updateGlobleSaveData(globleSaveData.RUBRIC.name, {
        data: rubricHtml,
        Id: newRubricId ?? null,
      });
      setPreviesRubric(true);
      if (newRubricId != null) {
        setEachRubric_id(newRubricId);
        setrubric_id(newRubricId);
        let existingIds = [];
        try {
          const raw = sessionStorage.getItem("Rubric_id");
          const parsed = raw ? JSON.parse(raw) : [];
          existingIds = Array.isArray(parsed) ? parsed : [];
        } catch {
          existingIds = [];
        }
        const idx =
          typeof rubricSlot === "number" &&
            !Number.isNaN(rubricSlot) &&
            rubricSlot >= 0
            ? rubricSlot
            : existingIds.length;
        const updatedIds = [...existingIds];
        while (updatedIds.length <= idx) {
          updatedIds.push(null);
        }
        updatedIds[idx] = newRubricId;
        sessionStorage.setItem("Rubric_id", JSON.stringify(updatedIds));
      }
      const lessonName = formStateRef.current?.name;
      if (lessonName) {
        sessionStorage.setItem("lesson_name", JSON.stringify(lessonName));
      }

      // Single refetch pass: getAssignment already chains get-rubric-by-lesson-id.
      // Calling afterCreateRubric as well duplicated every GET in the network tab.
      if (!generateAllRefetchDoneRef.current) {
        generateAllRefetchDoneRef.current = true;
        try {
          await generateAllRefetchAfterAssignmentRef.current?.(lessonPk);
        } catch (refetchErr) {
          // console.error(
          //   "Generate All refetch after create-rubric:",
          //   refetchErr,
          // );
        }

        try {
          const lessonList = await getLessons();
          const courseDetails = await getCourseDetails();
          sessionStorage.setItem(
            "previewLessonData",
            JSON.stringify({
              lesson: lessonList,
              course: courseDetails,
              teacher_id: params.get("teacher_id"),
            }),
          );
        } catch {
          // ignore
        }
      }

      generateAllPersistCompletedRef.current = true;
      setGenerateAllRubricEditorSync({
        html: rubricHtml,
        isHtml: true,
        id: Date.now(),
      });
      // requestAnimationFrame(() => {
      //   // setShowCourseSuccessfully(true)
      //   // setTimeout(() =>{
      //   //   setShowCourseSuccessfully(false)

      //   // },6000)

      //   setGenerateAllMode(false);
      // });
    } catch (err) {
      // console.error("flushGenerateAllPersist", err);
      const msg = err.response?.data?.message;
      if (msg) {
        toast.error(msg);
      }
      generateAllPersistStartedRef.current = false;
      setGenerateAllMode(false);
    }
  };

  useEffect(() => {
    const wasGenerating = prevGanrateLessonRef.current === true;
    prevGanrateLessonRef.current = ganrateLesson;

    if (!generateAllMode) return;
    if (!(wasGenerating && ganrateLesson === false)) return;

    const task = generateAllTaskNameRef.current;
    if (!task) return;

    const hist = chatHistRef.current;
    const rawAnswer = hist[hist.length - 1]?.answer ?? "";

    if (!rawAnswer) return;

    const html = markdownAnswerToHtml(rawAnswer);

    if (task === "Lesson") {
      updateWorkboardDataByName("Lesson", html);
      updateDefaultWorkboardData(defaultWorkboardData.LESSON.name, html);
      generateAllTaskNameRef.current = "Assignment";
      if (generateAllMode) {
        setAssignmentCounts(["Assignment 1"]);
        setAssignmentCountsTwo(0);
        setSelectedAssignment("Assignment 1");
      }
      setIsAssignment(true);
      setIsRubric(false);
      setActiveWorkboard(workboards.ASSIGNMENT);
      setFormAssignmentState((prev) => ({
        ...prev,
        name: `${formState.name} Assignment`,
        duration_minutes: 5,
        type: 7,
      }));
      assignmentDurationChangeTime(-999);
      assignmentDurationChangeTime(5);
      setAssignmentDurationCount(1);
      sessionStorage.setItem("assignmentDurationCount", "1");
      // queueMicrotask(() => {
      //   sendGenerateLession({ preventDefault() { } }, true);
      //   const p = persistGenerateAllLessonApi(html);
      //   generateAllLessonSavePromiseRef.current = p;
      //   void p.catch((err) => {
      //     // console.error(err);
      //     const msg = err.response?.data?.message ?? err.message;
      //     if (msg) toast.error(msg);
      //     setGenerateAllMode(false);
      //     generateAllTaskNameRef.current = null;
      //   });
      // });

      queueMicrotask(() => {
        // Persist the lesson first; only start Assignment generation once the
        // create/update-lesson API resolves successfully.
        const p = persistGenerateAllLessonApi(html);
        generateAllLessonSavePromiseRef.current = p;
        p.then(() => {
          sendGenerateLession({ preventDefault() { } }, true);
        }).catch((err) => {
          const msg = err.response?.data?.message ?? err.message;
          if (msg) toast.error(msg);
          setGenerateAllMode(false);
          generateAllTaskNameRef.current = null;
        });
      });
      return;
    }

    if (task === "Assignment") {
      updateWorkboardDataByName("Assignment", html);
      updateDefaultWorkboardData(defaultWorkboardData.ASSIGNMENT.name, html);
      generateAllTaskNameRef.current = "Rubric";
      if (generateAllMode) {
        setRubricCount(["Rubric 1"]);
        setRubricCountTwo(0);
        setSelectedRubric("Rubric 1");
      }
      setIsAssignment(false);
      setIsRubric(true);
      setActiveWorkboard(workboards.RUBRIC);
      // queueMicrotask(() => {
      //   sendGenerateLession({ preventDefault() { } }, true);
      //   const p = persistGenerateAllAssignmentApi(html);
      //   generateAllAssignmentSavePromiseRef.current = p;
      //   void p.catch((err) => {
      //     // console.error(err);
      //     const msg = err.response?.data?.message ?? err.message;
      //     if (msg) toast.error(msg);
      //     setGenerateAllMode(false);
      //     generateAllTaskNameRef.current = null;
      //   });
      // });

      queueMicrotask(() => {
        // Persist the assignment first; only start Rubric generation once the
        // create-assignment API resolves successfully.
        const p = persistGenerateAllAssignmentApi(html);
        generateAllAssignmentSavePromiseRef.current = p;
        p.then(() => {
          sendGenerateLession({ preventDefault() { } }, true);
        }).catch((err) => {
          const msg = err.response?.data?.message ?? err.message;
          if (msg) toast.error(msg);
          setGenerateAllMode(false);
          generateAllTaskNameRef.current = null;
        });
      });
      return;
    }

    if (task === "Rubric") {
      updateWorkboardDataByName("Rubric", html);
      updateDefaultWorkboardData(defaultWorkboardData.RUBRIC.name, html);
      generateAllTaskNameRef.current = null;
      queueMicrotask(() => {
        void flushGenerateAllPersist();
      });
    }
  }, [ganrateLesson, chatHist, generateAllMode]);

  const lessonbuttonStylebackgroundColor =
    workboards.LESSON.data !== "" ? "#b5ff9fcc" : "transparent";

  const lessonbuttonStyle = {
    background: lessonbuttonStylebackgroundColor,
    color: activeWorkboard.name === "Lesson" ? "#0068ff" : "black",
    // You can add other styles here if needed
  };

  // Function to open the modal
  const openModal = () => {
    setshowDownloadFile(true);
  };

  const toggledownlodefile = () => {
    setFileNameDownload("");
    setFileNameError("");
    setshowDownloadFile(false);
  };

  // const exportToWord = async () => {
  //   if (fileNameDownload === null || fileNameDownload === "") {
  //     setFileNameError("Please enter filename");
  //   } else {
  //     const preHtml =
  //       "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Export HTML To Word</title></head><body>";
  //     const postHtml = "</body></html>";
  //     // Get the original content
  //     let editorContent = getEditorContent();

  //     // Remove the close image spans before creating final HTML
  //     editorContent = removeCloseImageSpans(editorContent);
  //     const html = preHtml + editorContent + postHtml;

  //     const blob = new Blob(["\ufeff", html], {
  //       type: "application/msword",
  //     });

  //     const url =
  //       "data:application/vnd.ms-word;charset=utf-8," +
  //       encodeURIComponent(html);
  //     const fileName = fileNameDownload + ".doc";

  //     const downloadLink = document.createElement("a");
  //     document.body.appendChild(downloadLink);

  //     if (navigator.msSaveOrOpenBlob) {
  //       navigator.msSaveOrOpenBlob(blob, fileName);
  //     } else {
  //       downloadLink.href = url;
  //       downloadLink.download = fileName;
  //       downloadLink.click();
  //     }
  //     document.body.removeChild(downloadLink);
  //     toggledownlodefile();
  //   }
  // };

  const exportToWord = async () => {
    if (fileNameDownload === null || fileNameDownload === "") {
      setFileNameError("Please enter filename");
      return;
    }

    let editorContent = getEditorContent();

    // Remove the close image spans before creating final HTML
    editorContent = removeIframeCloseButtons(editorContent);
    editorContent = removeCloseImageSpans(editorContent);

    try {
      setDownloadLoading(true);
      const response = await api.post(
        `auth/convert-html-to-pdf/`,
        { html_text: editorContent },
        {
          responseType: "blob", // Important for handling binary data
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${fileNameDownload}.pdf`); // or any other extension
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        toast.success("Download successful!");

        setFileNameDownload("");
        setFileNameError("");
        setshowDownloadFile(false);
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleFileNameChange = (e) => {
    const enteredValue = e.target.value.trim();

    setFileNameDownload(enteredValue); // Always update the state

    if (!enteredValue) {
      setFileNameError("Please enter filename");
    } else {
      setFileNameError("");
    }
  };
  const randomRoom_id = Number(sessionStorage.getItem("RandomNumber"));
  const {
    connectReformatterWebSocket,
    closeConnection: closeFormatterConnection,
    sendMessage,
  } = useReformatterWebSocket({
    wsURL,
    randomRoom_id,
    setGetReformatterSocket,
    SetReFormatterLoading,
    setReFormatterData,
    setReformatterReconnectAttempts,
    reformatterReconnectAttempts,
    shouldReconnectRef,
    token,
  });

  const handleEnhanceText = () => {
    SetReFormatterLoading(true);
    const payload = {
      lesson_text: workboards[activeWorkboard.name.toUpperCase()].data,
    };

    connectReformatterWebSocket(payload);
  };

  function OnChangeRubricPlugin({
    onChangeRubricData,
    rubricCounttTwo,
    generateAllMode: generateAllModeProp,
  }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
      if (generateAllModeProp) return undefined;

      const handleUpdate = ({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const htmlContent = $generateHtmlFromNodes(editor);
            const currentCleanedContent = stripStyles(htmlContent);
            const previousCleanedContent = stripStyles(
              onChangeRubricData[rubricCounttTwo] || "",
            );
            // Only call the update functions if the content has changed
            if (
              currentCleanedContent !== previousCleanedContent ||
              htmlContent === ""
            ) {
              setDisabledRubric(false);

              updateWorkboardDataByName(activeWorkboard.name, htmlContent);

              updateGlobleOnChangeData(activeWorkboard.name, htmlContent);
            } else {
              updateWorkboardDataByName(
                activeWorkboard.name,
                onChangeRubricData[rubricCounttTwo],
              );
              updateGlobleOnChangeData(
                activeWorkboard.name,
                onChangeRubricData[rubricCounttTwo],
              );
              setDisabledRubric(true);
            }
          }
        });
      };

      const unregisterUpdateListener =
        editor.registerUpdateListener(handleUpdate);

      return () => {
        unregisterUpdateListener();
      };
    }, [editor, generateAllModeProp]);

    return null;
  }

  function OnChangePlugin({
    activeWorkboard,
    editorActiveWorkboardNameRef,
    globleOnChangeDataSave,
    isNotSavePopUp,
    setGlobleOnChangeDataSave,
    updateGlobleOnChangeData,
    updateWorkboardDataByName,
    setShowInputTooltip,
    setLatextextvalue,
    setOldMathNode,
  }) {
    useLexicalMathNodeHandler({
      activeWorkboard,
      editorActiveWorkboardNameRef,
      globleOnChangeDataSave,
      isNotSavePopUp,
      setGlobleOnChangeDataSave,
      updateGlobleOnChangeData,
      updateWorkboardDataByName,
      setShowInputTooltip,
      setLatextextvalue,
      setOldMathNode,
      hasSeededLessonRef,
      location,
    });

    return null; // Plugin doesn’t render anything visible
  }

  const onCoursePublishToSchoology = async (toggle, newcoursestatus, getid) => {
    //setshowloading(true)//bb
    setLoadingPublishSchoology(true); // Start loader
    // setisSaveChecker(true); // Disable the button

    try {
      const req = {
        assignment_id: getid,
        toggle: toggle,
      };

      // Dispatch the Redux action and wait for its completion
      const response = dispatch(
        setcourseCheckPublishtoSchoologydtls(req, token),
      );
    } catch (error) {
      // console.error("API Error:", error);
      //  setshowloading(false)
      setShowPopUp(true);
    } finally {
      setLoadingPublishSchoology(false); // Stop loader
      setisSaveChecker(false); // Re-enable the button
      //  setshowloading(false)
    }
  };

  const handleForm1Submit = async (e, schoologystatus) => {
    e.preventDefault();
    if (isSaveChecker) {
      return; // If the button is already disabled, do nothing
    }
    setisSaveChecker(true);
    setLoading(true);
    setEditLoading(true);

    const { name, start_date } = formState;
    toast.dismiss();

    if (!name) {
      toast.error("Please enter Lesson Name.", {
        position: "top-center",
      });
      setLoading(false);
      setisSaveChecker(false);
      return;
    } else if (name && name.length > 75) {
      toast.error(
        "Please don't enter more than 75 characters in Lesson Name.",
        {
          position: "top-center",
        },
      );
      setLoading(false);
      setisSaveChecker(false);
      return;
    } else if (name.trim() === "") {
      toast.error("Please enter Lesson Name.", {
        position: "top-center",
      });
      setLoading(false);
      setisSaveChecker(false);
      return;
    }
    toast.dismiss();
    if (!start_date) {
      toast.error("Please enter a valid start date.", {
        position: "top-center",
      });
      setLoading(false);
      setisSaveChecker(false);
      return;
    } else {
      try {
        const url =
          lesson_id !== ""
            ? `lessons/update-lesson/${lesson_id}/`
            : "lessons/create-lesson/";

        const roomID = sessionStorage.getItem("roomId");

        let payload;
        //bb started from here
        const SL = sessionStorage.getItem("skipLesson");

        if (SL === "true") {
          payload = {
            name: formState?.name ?? "",
            start_date: formState?.start_date ?? "",
            end_date: formState?.end_date,
            description: null,
            course: course?.course_id ?? "",
            teacher: teacher_id,
            status: 3,
            user_query: { question: chatHist[0]?.query ?? "" },
            ai_answer: { answer: chatHist[0]?.answer ?? "" },
            room_id: roomID,
            //room_id: room_id,
            is_personalize: isSwitchOn,
            is_file_download: isSwitchOn2,
            lesson_preview: isSwitchOnLessonPreview,
            virtual_assistant: isSwitchOnVirtualAssistant,
            is_skip: true, //bb
            ...getLowerSchoolPayload(),
            // push_to_schoology: schoologystatus == 'syncAttempt' ? true : false,
          };
        } else {
          payload = {
            name: formState?.name ?? "",
            start_date: formState?.start_date ?? "",
            end_date: formState?.end_date,
            description: removeCloseImageSpans(workboards.LESSON.data),
            course: course?.course_id ?? "",
            teacher: teacher_id,
            status: 3,
            user_query: { question: chatHist[0]?.query ?? "" },
            ai_answer: { answer: chatHist[0]?.answer ?? "" },
            room_id: roomID,
            //room_id: room_id,
            is_personalize: isSwitchOn,
            is_file_download: isSwitchOn2,
            lesson_preview: isSwitchOnLessonPreview,
            virtual_assistant: isSwitchOnVirtualAssistant, // push_to_schoology: schoologystatus == 'syncAttempt' ? true : false,
            ...getLowerSchoolPayload(),
          };
        }

        const headers = {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        };

        const response =
          lesson_id !== ""
            ? await api.put(url, payload, { headers: headers })
            : await api.post(url, payload, { headers: headers });

        if (response.status === 200 || response.status === 201) {
          sessionStorage.setItem(
            "lesson_name",
            JSON.stringify(formState?.name),
          );

          setisSaveChecker(false);
          if (skipLesson) {
            setShowCourseSuccessfully(false);
          } else {
            setShowCourseSuccessfully(true);
          }
          setLoadingPublishSchoology(false);

          const getlesson_id = response?.data?.data?.lesson_id;
          const lessonData = {
            Id: getlesson_id,
            data:
              response?.data?.data?.description ===
                EMPTY_LESSON_PLACEHOLDER_DESCRIPTION_two
                ? ""
                : response?.data?.data?.description,
          };

          sessionStorage.setItem("lessonDetails", JSON.stringify(lessonData));

          updateDefaultWorkboardData(
            defaultWorkboardData.LESSON.name,
            response?.data?.data?.description ===
              EMPTY_LESSON_PLACEHOLDER_DESCRIPTION_two
              ? ""
              : response?.data?.data?.description || "",
          );
          const lessonDescFromApi =
            response?.data?.data?.description ===
              EMPTY_LESSON_PLACEHOLDER_DESCRIPTION_two
              ? ""
              : response?.data?.data?.description;
          if (lessonDescFromApi != null) {
            updateWorkboardDataByName(
              workboards.LESSON.name,
              lessonDescFromApi,
            );
          }
          setLesson(lessonData);
          updateGlobleSaveData(activeWorkboard.name, lessonData);

          // update previoursLessonData
          const lesson = await getLessons();
          const course = await getCourseDetails();

          const data = {
            lesson,
            course,
            teacher_id: params.get("teacher_id"),
          };

          // Don't mutate location.state directly — instead, store your data
          sessionStorage.setItem("previewLessonData", JSON.stringify(data));

          // After saving the lesson, auto-create 1 assignment and switch to the Assignment section.
          // `UpdateActiveWorkboard` listens to this flag and performs the same flow as clicking "Add assignment".

          setTimeout(() => {
            setShowCourseSuccessfully(false);
            document.body.style.overflow = "auto";
            if (lesson_id === "") {
              setlesson_id(getlesson_id);
            }
          }, 1000);
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        setisSaveChecker(false);
      }
    }
  };

  const handleSwitchToggleSchoologySync = (e) => {
    setshowloading(true);
    const updatedAssignments = [...getassignmentdetails];
    updatedAssignments[assignmentsCountTwo] = {
      ...updatedAssignments[assignmentsCountTwo],
      synced_with_schoology: e.target.checked, // Update toggle state
    };
    setGetAssignmentdetails(updatedAssignments); // Update the assignments state
    setIstoggleClick(true); // Set the toggle click state to true
    setswitchschoologySync(e.target.checked);

    if (e.target.checked) {
      if (!isNotSavePopUp) {
        e.preventDefault();
      }

      setLoadingPublishSchoology(true);
      let schoologystatus = "syncAttemptStart";

      if (lesson_id !== "") {
        if (eachAssignment_id === null) {
          // handleAssinmentRubricSubmit(
          //   e,
          //   schoologystatus
          // );
          setschoologystatus(schoologystatus);
        } else {
          onCoursePublishToSchoology(
            e.target.checked,
            false,
            eachAssignment_id,
          );
          setschoologystatus(schoologystatus);
        }
      } else {
        handleForm1Submit(e, schoologystatus);
        setschoologystatus(schoologystatus);
      }
    } else {
      let schoologystatus = "syncAttemptStart";

      // Ensure that assignment_id is passed, even when toggling off
      onCoursePublishToSchoology(e.target.checked, false, eachAssignment_id);

      setGetAssignmentdetails((prevAssignments) => {
        // Create a new array by mapping through the previous state
        return prevAssignments.map((assignment, index) => {
          // If this is the item we want to update
          if (index === assignmentsCountTwo) {
            // Return a new object with the updated property
            return { ...assignment, synced_with_schoology: false };
          }
          // Otherwise return the original item
          return assignment;
        });
      });
      setschoologystatus(schoologystatus);
    }
  };

  const LessonSkipComponent = () => (
    <LessonSkipPopup
      skipLesson={skipLesson}
      setSkipLesson={setskipLesson}
      setskipLessonflag={setskipLessonflag}
      handleForm1Submit={handleForm1Submit}
      setPopupVisible={setPopupVisible}
      setFormAssignmentState={setFormAssignmentState}
      setFormErrorAssignmentState={setFormErrorAssignmentState}
      workboards={workboards}
      setActiveWorkboard={setActiveWorkboard}
      setActiveTab={setActiveTab}
      setIsAssignment={setIsAssignment}
      setIsRubric={setIsRubric}
      setAssignmentCounts={setAssignmentCounts}
      setAssignmentCountsTwo={setAssignmentCountsTwo}
      setSelectedAssignment={setSelectedAssignment}
      setFormState={setFormState}
      checkAssignmentopen={checkAssignmentopen}
      assignmentsCount={assignmentsCount}
      setskipLessonSession={setskipLessonSession}
    />
  );

  async function getLessons() {
    let lessonSingle = {};
    const url = `lessons/get-all-lessons/course/${params.get("course_id")}/`;
    try {
      const response = await api.get(url, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Token ${token}`,
        },
      });
      if (response.status === 200) {
        if (response?.data?.data.length > 0) {
          const filteredLesson = response?.data?.data.find((lesson) => {
            return lesson.lesson_id === Number(params.get("lesson_id"));
          });

          const updatedLesson = {
            ...filteredLesson,
            description:
              filteredLesson.description ===
                EMPTY_LESSON_PLACEHOLDER_DESCRIPTION_two
                ? ""
                : filteredLesson.description,
          };
          lessonSingle = updatedLesson || {};
        }
      }
    } catch (error) {
      // handle error if needed
    }

    return lessonSingle;
  }

  async function getCourseDetails() {
    const url = `courses/get-course/${params.get("course_id")}/`;
    try {
      const response = await api.get(url, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Token ${token}`,
        },
      });
      if (response.status === 200) {
        return response.data.data;
      }
    } catch (error) {
      // handle error if needed
    }
    return null;
  }

  useEffect(() => {
    const fetchCourse = async () => {
      const getCourse = await getCourseDetails();

      setMyRole(getCourse?.my_role);
      setCourseLanguage(getCourse?.language || DEFAULT_DICTATION_LANGUAGE);
    };

    fetchCourse();
  }, []);

  // In your context or component
  const handleSwitchToggleHideTimer = () => {
    if (isAssignment) {
      if (previesAssignment) {
        const payload = {
          hide_timer: !currentHideTimerState,
        };
        updateLesson(eachAssignment_id, payload, "assignment", "Hide Timer");
      }

      const newState = !currentHideTimerState;
      setCurrentHideTimerState(newState);
      // setGetAssignmentdetails([...prevAssignments])
      setHideTimerStates((prev) => {
        const updated = {
          ...prev,
          [assignmentsCountTwo]: newState,
        };
        // console.log('🔍 Updated hideTimerStates:', updated);
        return updated;
      });
    }
  };

  const handleSaveHideTimer = () => {
    let prevAssignments = [...getassignmentdetails];
    prevAssignments[assignmentsCountTwo] = {
      ...prevAssignments[assignmentsCountTwo],
      hide_timer: hideTimerStates[assignmentsCountTwo],
    };
    // setGetAssignmentdetails([...prevAssignments]);
  };

  useEffect(() => {
    if (
      isAssignment &&
      getassignmentdetails &&
      getassignmentdetails[assignmentsCountTwo]
    ) {
      const assignment = getassignmentdetails[assignmentsCountTwo];
      const backendValue = assignment.hide_timer || false;

      // Check if user has an unsaved in-memory state for this assignment
      const hasUnsavedState =
        hideTimerStates.hasOwnProperty(assignmentsCountTwo);

      if (hasUnsavedState) {
        // User toggled but didn't save — restore their unsaved toggle state
        setCurrentHideTimerState(hideTimerStates[assignmentsCountTwo]);
      } else {
        // No unsaved state — load from backend
        setCurrentHideTimerState(backendValue);
        setHideTimerStates((prev) => ({
          ...prev,
          [assignmentsCountTwo]: backendValue,
        }));
      }
    }
  }, [getassignmentdetails, assignmentsCountTwo, isAssignment]);

  // Only handle the case where there's NO backend data yet (new/unsaved assignment)
  useEffect(() => {
    if (isAssignment && assignmentsCountTwo !== undefined) {
      const hasBackendData =
        getassignmentdetails && getassignmentdetails[assignmentsCountTwo];

      // Only fall back to in-memory state if no backend data exists
      if (!hasBackendData) {
        const savedState = hideTimerStates[assignmentsCountTwo] || false;
        setCurrentHideTimerState(savedState);
      }
    } else if (!isAssignment) {
      setCurrentHideTimerState(false);
    }
  }, [assignmentsCountTwo, isAssignment]); // removed hideTimerStates from deps to avoid loop

  // 3️⃣ Values shared across components
  const value = {
    room_id,
    setRoom_id,
    lesson,
    setLesson,
    formState,
    setFormState,
    formLessonError,
    setFormLessonErrorState,
    editorConfig,
    chatHist,
    setChatHist,
    isChatHistLoading,
    setIsChatHistLoading,
    selectedFiles,
    setSelectedFiles,
    progressTracker,
    setprogressTracker,
    reFormatterLoading,
    SetReFormatterLoading,
    isGenerating,
    setIsGenerating,
    showScrollButton,
    setShowScrollButton,
    messagesEndRef,
    handleInputChange,
    TabToBottom,
    minDate,
    isValidDateAssignmentStartDate,
    isValidDateAssignmentEndDate,
    isValidDateLessonStartDate,
    isValidDateLessonEndDate,
    formAssignmentState,
    setFormAssignmentState,
    formErrorAssignmentState,
    setFormErrorAssignmentState,
    lessonMinDate,
    course,
    handleInputChangedate,
    pop_up_for_lesson_start_date_note,
    setPopupForLessonStartDateNote,
    lesson_id,
    setlesson_id,
    isLessonSaved,
    isLockedBySubmissions,
    SUBMISSION_LOCKED_MESSAGE,
    setIsLessonSaved,
    isSmallScreen,
    activeTab,
    setActiveTab,
    isSwitchOnVirtualAssistant,
    setIsSwitchOnVirtualAssistant,
    handleSwitchToggleVirtualAssistant,
    Assignmentgeneration,
    setAssignmentgeneration,
    handleSwitchToggleDownload,
    handleSwitchToggleLessonPreview,
    isSwitchOn2,
    isSwitchOnLessonPreview,
    handleScrollClick,
    getname,
    globalState,
    generateAIVoice,
    texttospeech,
    chatAudioId,
    onCopyClicked,
    workboardText,
    setWorkboardText,
    activeWorkboard,
    setActiveWorkboard,
    editorActiveWorkboardNameRef,
    LessonSkipComponent,
    syncEditorActiveWorkboardName,
    workboards,
    setWorkboards,
    rubrics,
    setRubrics,
    assignments,
    setAssignments,
    isAssignment,
    setIsAssignment,
    isRubric,
    setIsRubric,
    uploadedFileLink,
    setuploadedFileLink,
    fileInputRef,
    sendGenerateLession,
    wsURL,
    teacher_id,
    scrollChatContainerToBottom,
    setShowCloseBTN,
    setGanrateLesson,
    closeConnection,
    socket,
    isLessonSocketReady,
    combinedMessageRef,
    showCloseBTN,
    ganrateLesson,
    generateAllMode,
    setGenerateAllMode,
    isPopupVisible,
    showPopup,
    setPopupVisible,
    SkipLessonflag,
    setskipLessonflag,
    setIsSwitchOn,
    isSwitchOn,
    setShowPopup,
    setchatAudioId,
    checkAssignmentopen,
    setCheckAssignmentOpen,
    handleAssignmentInputChange,
    assignmentDurationChangeTime,
    assignmentDuration,
    setAssignmentDuration,
    setAssignmentDurationCount,
    assignmentDurationCount,
    handleAssignmentInputChangedate,
    showUploadPop,
    setShowUploadPop,
    textBoxRef,
    showUploadPopupcall,
    isListening,
    transcript,
    startListening,
    stopListening,
    startStopListening,
    stopVoiceInput,
    handleInputKeyPress,
    showstudentviewDtls,
    openStudentView,
    previewDtls,
    token,
    rubric_id,
    assignment_id,
    previesAssignment,
    previesRubric,
    eachAssignment_id,
    eachRubric_id,
    defaultWorkboardData,
    globleSaveData,
    getassignmentdetails,
    assignmentsCountTwo,
    rubricCounttTwo,
    setIstoggleClick,
    setGlobleOnChangeDataSave,
    setClickSaveBTN,
    setClickSaveRubricBTN,
    setLoading,
    setisSaveChecker,
    setShowCourseSuccessfully,
    setLoadingPublishSchoology,
    updateDefaultWorkboardData,
    updateGlobleSaveData,
    setPreviesAssignment,
    setPreviesRubric,
    setEachAssignment_id,
    setEachRubric_id,
    setassignment_id,
    setrubric_id,
    setAssingmentListOfID,
    setGlobleSaveData,
    isRequired,
    handleSwitchToggle,
    isSaveChecker,
    switchschoologySync,
    setswitchschoologySync,
    handleAssinmentRubricSubmit,
    assingmentListOfID,
    istoggleClick,
    globleOnChangeDataSave,
    clickSaveBTN,
    clickSaveRubricBTN,
    loading,
    showCourseSuccessfully,
    loadingPublishSchoology,
    setGetAssignmentdetails,
    setAssignmentCountsTwo,
    setRubricCountTwo,
    setpreviewDtls,
    setopenStudentView,
    disabledRubric,
    setDisabledRubric,
    assignmentsCount,
    setAssignmentCounts,
    showAssingment,
    selectedAssignment,
    setShowAssingment,
    setSelectedAssignment,
    onChangeAssignmentData,
    setOnChangeAssignmentData,
    editWorkboardStatues,
    setEditWorkboardStatues,
    rubricCount,
    setRubricCount,
    onChangeRubricData,
    setOnChangeRubricData,
    selectedRubric,
    setSelectedRubric,
    showDropdownRubric,
    setShowDropdownRubric,
    updateFuntion,
    setUpdateFuntion,
    isRubricConfirmModalData,
    setIsRubricConfirmModalData,
    isAssingmentConfirmModalData,
    setIsAssingmentConfirmModalData,
    afterConfirmation,
    setAfterConfirmation,
    callAddAssignmentFun,
    setCallAddAssignmentFun,
    callSwitchToRubricFun,
    setCallSwitchToRubricFun,
    pendingAssignmentClickArgs,
    setPendingAssignmentClickArgs,
    updateWorkboardDataByName,
    lessonbuttonStyle,
    globleOnChange,
    setGlobleOnChange,
    updateGlobleOnChangeData,
    showRubricPopUp,
    setShowRubricPopUp,
    showDropdown,
    setShowDropdown,
    showDownloadFile,
    setshowDownloadFile,
    openModal,
    modalRef,
    modalContentRef,
    fileNameError,
    setFileNameError,
    fileNameDownload,
    setFileNameDownload,
    toggledownlodefile,
    exportToWord,
    handleFileNameChange,
    getschoologystatus,
    setschoologystatus,
    showInputTooltip,
    setShowInputTooltip,
    latextextvalue,
    setLatextextvalue,
    oldMathNode,
    setOldMathNode,
    oldnodeeditor,
    setOldNodeEditor,
    shouldReconnectRef,
    reformatterReconnectAttempts,
    connectReformatterWebSocket,
    closeFormatterConnection,
    sendMessage,
    setGetReformatterSocket,
    reFormatterData,
    setReFormatterData,
    getReformatterSocket,
    setReformatterReconnectAttempts,
    handleEnhanceText,
    OnChangeRubricPlugin,
    OnChangePlugin,
    handleSwitchToggleSchoologySync,
    showloading,
    setshowloading,
    editloading,
    setEditLoading,
    skipLesson,
    setskipLesson,
    handleForm1Submit,
    showPopUp,
    setShowPopUp,
    isNotSavePopUp,
    setIsNotSavePopUp,
    location,
    getLessons,
    getCourseDetails,
    showLesson,
    handleUserScroll,
    chatContainerRef,
    scrollChatToElement,
    adminRole,
    chatDrawerState,
    handleSwitchToggleHideTimer,
    currentHideTimerState,
    handleSaveHideTimer,
    selectedRepoFiles,
    setSelectedRepoFiles,
    defaultRepoFiles,
    setDefaultRepoFiles,
    handleGenerateAll,
    flushGenerateAllPersist,
    registerGenerateAllRefetchCallbacks,
    chatHistRef,
    generateAllRubricEditorSync,
    setGenerateAllRubricEditorSync,
    toggleLoader,
    setskipLessonSession,
    skipLessonSession,
    getArraySectionData,
    cleanHtml,
    getSectionData,
    setIsSaveLoading,
    isSaveLoading,
    myRole,
    setYoutubeLinks,
    youtubeLinks,
    youtubeLinksRef,
    setGetSavedYoutubeLinks,
    getSavedYoutubeLinks,
    socketDisconnected,
    setSocketDisconnected,
    stopGenerateAllMode,
    createManuallyMode,
    setCreateManuallyMode,
    generationType,
    setGenerationType,
    downloadLoading,
    ...storySetup,
    typeChanged,
    setTypeChanged,
    setGetSpecificAssignmentType,
    getSpecificAssignmentType,
    generateImageOpen,
    setGenerateImageOpen,
    isWorkboardEmpty,
    defaultYoutubeLink
  };

  return (
    <NewLessonsContext.Provider value={value}>
      {children}
    </NewLessonsContext.Provider>
  );
};

// 4️⃣ Create a custom hook for easy access
export const useNewLessons = () => {
  return useContext(NewLessonsContext);
};
